import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { BACKEND_URL as CONFIG_BACKEND_URL } from '../config/api';

const BACKEND_URL = CONFIG_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const CustomerPortal = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [accountInfo, setAccountInfo] = useState(null);
  
  // Registration state
  const [showRegister, setShowRegister] = useState(false);
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerConfirm, setRegisterConfirm] = useState('');
  const [registerError, setRegisterError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState(false);
  
  // Forgot Password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotError, setForgotError] = useState('');
  
  // Show password state
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showRegisterConfirm, setShowRegisterConfirm] = useState(false);
  
  // Remember me state
  const [rememberMe, setRememberMe] = useState(false);
  
  // Portal state
  const [activeTab, setActiveTab] = useState('dashboard');
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Order filter/search state
  const [orderFilter, setOrderFilter] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  
  // Messages state
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // File upload state
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // New Order state - Enhanced with full vehicle selection
  const [newOrderStep, setNewOrderStep] = useState(1); // 1: Vehicle, 2: Upload, 3: Analysis, 4: Services, 5: Confirm
  const [newOrderFile, setNewOrderFile] = useState(null);
  const [newOrderServices, setNewOrderServices] = useState([]);
  const [newOrderNotes, setNewOrderNotes] = useState('');
  const [additionalDtcCodes, setAdditionalDtcCodes] = useState('');
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [showDtcSelector, setShowDtcSelector] = useState(false);
  const [selectedFileDtcs, setSelectedFileDtcs] = useState([]);
  
  // Quick Order state
  const [showQuickOrder, setShowQuickOrder] = useState(false);
  const [quickOrderFile, setQuickOrderFile] = useState(null);
  const [quickOrderPrevOrder, setQuickOrderPrevOrder] = useState(null);
  const [quickOrderLoading, setQuickOrderLoading] = useState(false);
  const quickOrderFileRef = useRef(null);
  
  // Vehicle Selection State
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [models, setModels] = useState([]);
  const [engines, setEngines] = useState([]);
  
  const [selectedVehicleType, setSelectedVehicleType] = useState('');
  const [selectedManufacturer, setSelectedManufacturer] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedEngine, setSelectedEngine] = useState('');
  const [selectedEcu, setSelectedEcu] = useState('');
  const [customEcu, setCustomEcu] = useState('');
  const [dynamicEcuTypes, setDynamicEcuTypes] = useState([]);
  const [ecuLoading, setEcuLoading] = useState(false);
  
  // Manual vehicle entry
  const [isManualVehicle, setIsManualVehicle] = useState(false);
  const [manualVehicle, setManualVehicle] = useState({ make: '', model: '', year: '', engine: '' });
  
  // Vehicle loading states
  const [vehicleLoading, setVehicleLoading] = useState(false);
  
  // ECU Analysis State
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [detectedServices, setDetectedServices] = useState([]);
  const [fileId, setFileId] = useState(null);
  
  // DTC Removal Request state
  const [dtcRequestCodes, setDtcRequestCodes] = useState('');
  const [dtcRequestNotes, setDtcRequestNotes] = useState('');
  const [submittingDtcRequest, setSubmittingDtcRequest] = useState(false);
  const [dtcRequests, setDtcRequests] = useState([]);
  const [loadingDtcRequests, setLoadingDtcRequests] = useState(false);
  
  // Payment state for orders
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  
  // Common ECU types for manual entry
  const commonEcuTypes = [
    { id: 'bosch-edc17c50', name: 'Bosch EDC17C50' },
    { id: 'bosch-edc17c46', name: 'Bosch EDC17C46' },
    { id: 'bosch-edc17c54', name: 'Bosch EDC17C54' },
    { id: 'bosch-edc17c64', name: 'Bosch EDC17C64' },
    { id: 'bosch-edc16', name: 'Bosch EDC16' },
    { id: 'bosch-med17', name: 'Bosch MED17' },
    { id: 'bosch-me7', name: 'Bosch ME7' },
    { id: 'siemens-sid201', name: 'Siemens SID201' },
    { id: 'siemens-sid206', name: 'Siemens SID206' },
    { id: 'siemens-sid803', name: 'Siemens SID803' },
    { id: 'continental-simos18', name: 'Continental Simos 18' },
    { id: 'delphi-dcm35', name: 'Delphi DCM3.5' },
    { id: 'denso', name: 'Denso' },
    { id: 'marelli', name: 'Marelli' },
    { id: 'other', name: 'Other / Unknown' },
  ];
  
  // Settings state
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [settingsMessage, setSettingsMessage] = useState({ type: '', text: '' });

  // Available services - matching DTC Delete Tool pricing
  const availableServices = [
    { id: 'dtc-single', name: 'DTC Removal (1 Code)', price: 10 },
    { id: 'dtc-multiple', name: 'DTC Removal (2-6 Codes)', price: 20 },
    { id: 'dtc-bulk', name: 'DTC Removal (7+ Codes)', price: 30 },
    { id: 'egr-removal', name: 'EGR Removal', price: 50 },
    { id: 'dpf-removal', name: 'DPF Removal', price: 248 },
    { id: 'egr-dpf-combo', name: 'EGR + DPF Combo', price: 248 },
    { id: 'adblue-removal', name: 'AdBlue/DEF Removal', price: 698 },
    { id: 'immo-off', name: 'Immobilizer Off', price: 70 },
    { id: 'decat', name: 'Decat (Cat OFF)', price: 40 },
    { id: 'vmax-off', name: 'Speed Limiter OFF', price: 30 },
    { id: 'checksum', name: 'Checksum Correction', price: 5 },
    { id: 'swirl-flap-off', name: 'Swirl Flap OFF', price: 40 },
    { id: 'nox-off', name: 'NOX Sensor OFF', price: 40 },
    { id: 'start-stop-off', name: 'Start & Stop OFF', price: 40 },
  ];

  // Load saved credentials on component mount (Remember Me feature)
  useEffect(() => {
    const savedEmail = localStorage.getItem('portal_email');
    const savedPassword = localStorage.getItem('portal_password');
    const savedRemember = localStorage.getItem('portal_remember');
    
    if (savedRemember === 'true' && savedEmail) {
      setLoginEmail(savedEmail);
      if (savedPassword) {
        setLoginPassword(savedPassword);
      }
      setRememberMe(true);
    }
  }, []);

  // Fetch vehicle types on component mount
  useEffect(() => {
    const fetchVehicleTypes = async () => {
      try {
        const response = await axios.get(`${API}/vehicles/types`);
        setVehicleTypes(response.data || []);
      } catch (error) {
        console.error('Error fetching vehicle types:', error);
      }
    };
    fetchVehicleTypes();
  }, []);

  // Fetch manufacturers when vehicle type changes
  const handleVehicleTypeChange = async (typeId) => {
    setSelectedVehicleType(typeId);
    setSelectedManufacturer('');
    setSelectedModel('');
    setSelectedEngine('');
    setSelectedEcu('');
    setManufacturers([]);
    setModels([]);
    setEngines([]);
    
    if (typeId === 'other') {
      setIsManualVehicle(true);
      return;
    }
    
    setIsManualVehicle(false);
    if (!typeId) return;
    
    setVehicleLoading(true);
    try {
      const response = await axios.get(`${API}/vehicles/manufacturers/${typeId}`);
      setManufacturers(response.data || []);
    } catch (error) {
      console.error('Error fetching manufacturers:', error);
    }
    setVehicleLoading(false);
  };

  // Fetch models when manufacturer changes
  const handleManufacturerChange = async (manufacturerId) => {
    setSelectedManufacturer(manufacturerId);
    setSelectedModel('');
    setSelectedEngine('');
    setSelectedEcu('');
    setModels([]);
    setEngines([]);
    
    if (!manufacturerId) return;
    
    setVehicleLoading(true);
    try {
      const response = await axios.get(`${API}/vehicles/models/${manufacturerId}`);
      setModels(response.data || []);
    } catch (error) {
      console.error('Error fetching models:', error);
    }
    setVehicleLoading(false);
  };

  // Fetch engines when model changes (skipping generations - not used in new DB structure)
  const handleModelChange = async (modelId) => {
    setSelectedModel(modelId);
    setSelectedEngine('');
    setSelectedEcu('');
    setEngines([]);
    
    if (!modelId) return;
    
    setVehicleLoading(true);
    try {
      // Fetch engines directly for the model (generations are skipped)
      const response = await axios.get(`${API}/vehicles/engines/${modelId}`);
      setEngines(response.data || []);
    } catch (error) {
      console.error('Error fetching engines:', error);
    }
    setVehicleLoading(false);
  };

  // Fetch ECU types when engine changes
  const handleEngineChange = async (engineId) => {
    setSelectedEngine(engineId);
    setSelectedEcu('');
    setCustomEcu('');
    setDynamicEcuTypes([]);
    
    if (engineId) {
      // Find the selected engine to get embedded ECUs
      const engine = engines.find(e => e.id === engineId);
      
      if (engine && engine.ecus && engine.ecus.length > 0) {
        // ECUs are embedded in the engine document (new structure)
        const ecuTypes = engine.ecus.map(ecu => ({
          id: ecu.id,
          name: ecu.name,
          manufacturer: ecu.name.split(' ')[0],
          has_dpf: ecu.has_dpf,
          has_egr: ecu.has_egr,
          has_adblue: ecu.has_adblue
        }));
        setDynamicEcuTypes(ecuTypes);
        
        // Auto-select if only one ECU
        if (ecuTypes.length === 1) {
          setSelectedEcu(ecuTypes[0].id);
        }
      } else {
        // Fallback to API call
        setEcuLoading(true);
        try {
          const response = await axios.get(`${API}/vehicles/ecu-types/${engineId}`);
          if (response.data && response.data.ecu_types) {
            setDynamicEcuTypes(response.data.ecu_types);
          }
        } catch (error) {
          console.error('Error fetching ECU types:', error);
          setDynamicEcuTypes([]);
        }
        setEcuLoading(false);
      }
    }
  };

  // Get vehicle summary for display
  const getVehicleSummary = () => {
    if (isManualVehicle) {
      const parts = [manualVehicle.year, manualVehicle.make, manualVehicle.model].filter(Boolean);
      return parts.length > 0 ? parts.join(' ') : 'Manual Entry';
    }
    
    const manufacturer = manufacturers.find(m => m.id === selectedManufacturer);
    const model = models.find(m => m.id === selectedModel);
    const engine = engines.find(e => e.id === selectedEngine);
    
    const parts = [
      manufacturer?.name || '',
      model?.name || '',
      engine?.name || ''
    ].filter(Boolean);
    
    return parts.join(' ') || 'Select Vehicle';
  };

  // Get ECU name for display
  const getEcuName = () => {
    if (selectedEcu === 'other') {
      return customEcu || 'Custom ECU';
    }
    // Check dynamic ECU types first (from API/engine selection)
    let ecu = dynamicEcuTypes.find(e => e.id === selectedEcu);
    if (ecu) return ecu.name;
    // Fallback to common ECU types
    ecu = commonEcuTypes.find(e => e.id === selectedEcu);
    return ecu?.name || '';
  };

  // Check if vehicle selection is complete
  const isVehicleComplete = () => {
    if (isManualVehicle) {
      const ecuValid = selectedEcu === 'other' ? customEcu.trim() : selectedEcu;
      return manualVehicle.make && manualVehicle.model && ecuValid;
    }
    const ecuValid = selectedEcu === 'other' ? customEcu.trim() : selectedEcu;
    return selectedVehicleType && selectedManufacturer && selectedModel && selectedEngine && ecuValid;
  };

  // Analyze uploaded file
  const analyzeFile = async () => {
    if (!newOrderFile) return;
    
    setAnalyzing(true);
    const formData = new FormData();
    formData.append('file', newOrderFile);
    
    // Add vehicle info
    if (isManualVehicle) {
      formData.append('vehicle_type', 'Other');
      formData.append('manufacturer', manualVehicle.make);
      formData.append('model', manualVehicle.model);
      formData.append('year', manualVehicle.year);
      formData.append('engine', manualVehicle.engine);
    } else {
      const vType = vehicleTypes.find(v => v.id === selectedVehicleType);
      const manufacturer = manufacturers.find(m => m.id === selectedManufacturer);
      const model = models.find(m => m.id === selectedModel);
      const engine = engines.find(e => e.id === selectedEngine);
      
      formData.append('vehicle_type', vType?.name || '');
      formData.append('manufacturer', manufacturer?.name || '');
      formData.append('model', model?.name || '');
      formData.append('year', ''); // Year extracted from engine/model if available
      formData.append('engine', engine?.name || '');
    }
    
    formData.append('ecu_type', getEcuName());
    
    try {
      const response = await axios.post(`${API}/analyze-and-process-file`, formData);
      
      if (response.data.success) {
        setFileId(response.data.file_id);
        setAnalysisResult(response.data);
        setDetectedServices(response.data.available_options || []);
        setNewOrderStep(4); // Move to services step
      } else {
        alert('Analysis failed: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error analyzing file:', error);
      alert('Error analyzing file. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  // Reset new order form
  const resetNewOrder = () => {
    setNewOrderStep(1);
    setNewOrderFile(null);
    setNewOrderServices([]);
    setNewOrderNotes('');
    setAdditionalDtcCodes('');
    setSelectedVehicleType('');
    setSelectedManufacturer('');
    setSelectedModel('');
    setSelectedEngine('');
    setSelectedEcu('');
    setIsManualVehicle(false);
    setManualVehicle({ make: '', model: '', year: '', engine: '' });
    setAnalysisResult(null);
    setDetectedServices([]);
    setFileId(null);
  };

  // Check URL params for auto-login
  useEffect(() => {
    const email = searchParams.get('email');
    const register = searchParams.get('register');
    
    if (register === 'true') {
      setShowRegister(true);
      if (email) setRegisterEmail(email);
    } else if (email) {
      setLoginEmail(email);
      doLoginEmail(email);
    }
  }, [searchParams]);

  // Fetch DTC requests when selected order changes
  useEffect(() => {
    if (selectedOrder && (selectedOrder.status === 'completed' || selectedOrder.status === 'processing' || selectedOrder.status === 'delivered' || selectedOrder.status === 'paid')) {
      fetchDtcRequests(selectedOrder.id);
    }
  }, [selectedOrder]);

  // Password-based login
  const doLoginPassword = async () => {
    if (!loginEmail || !loginPassword) {
      setLoginError('Please enter your email and password');
      return;
    }
    
    setLoginLoading(true);
    setLoginError('');
    
    try {
      const response = await axios.post(`${API}/portal/login-password`, {
        email: loginEmail.trim(),
        password: loginPassword
      });
      
      if (response.data.success) {
        // Save credentials if Remember Me is checked
        if (rememberMe) {
          localStorage.setItem('portal_email', loginEmail.trim());
          localStorage.setItem('portal_password', loginPassword);
          localStorage.setItem('portal_remember', 'true');
        } else {
          // Clear saved credentials if Remember Me is unchecked
          localStorage.removeItem('portal_email');
          localStorage.removeItem('portal_password');
          localStorage.removeItem('portal_remember');
        }
        
        setOrders(response.data.orders || []);
        setAccountInfo(response.data.account);
        setProfileName(response.data.account?.name || '');
        setIsLoggedIn(true);
        window.history.replaceState({}, '', `/portal?email=${encodeURIComponent(loginEmail)}`);
      }
    } catch (error) {
      setLoginError(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoginLoading(false);
    }
  };

  // Email-only login (quick access)
  const doLoginEmail = async (email) => {
    if (!email) {
      setLoginError('Please enter your email address');
      return;
    }
    
    setLoginLoading(true);
    setLoginError('');
    
    try {
      const response = await axios.post(`${API}/portal/login-email`, {
        email: email.trim()
      });
      
      if (response.data.success) {
        setOrders(response.data.orders || []);
        setAccountInfo(response.data.account || { email: email, name: 'Guest' });
        setIsLoggedIn(true);
        window.history.replaceState({}, '', `/portal?email=${encodeURIComponent(email)}`);
      }
    } catch (error) {
      setLoginError(error.response?.data?.detail || 'No orders found for this email');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e?.preventDefault();
    if (loginPassword) {
      doLoginPassword();
    } else {
      doLoginEmail(loginEmail);
    }
  };

  const handleRegister = async (e) => {
    e?.preventDefault();
    setRegisterError('');
    
    if (!registerName || !registerEmail || !registerPassword) {
      setRegisterError('Please fill in all fields');
      return;
    }
    
    if (registerPassword !== registerConfirm) {
      setRegisterError('Passwords do not match');
      return;
    }
    
    if (registerPassword.length < 6) {
      setRegisterError('Password must be at least 6 characters');
      return;
    }
    
    setLoginLoading(true);
    
    try {
      const response = await axios.post(`${API}/portal/register`, {
        name: registerName.trim(),
        email: registerEmail.trim(),
        password: registerPassword
      });
      
      if (response.data.success) {
        setRegisterSuccess(true);
        // Auto-login after 1.5 seconds
        setTimeout(async () => {
          setLoginEmail(registerEmail);
          setLoginPassword(registerPassword);
          setShowRegister(false);
          // Perform login directly with captured credentials
          try {
            const loginResponse = await axios.post(`${API}/portal/login-password`, {
              email: registerEmail.trim(),
              password: registerPassword
            });
            if (loginResponse.data.success) {
              setOrders(loginResponse.data.orders || []);
              setAccountInfo(loginResponse.data.account);
              setProfileName(loginResponse.data.account?.name || '');
              setIsLoggedIn(true);
              window.history.replaceState({}, '', `/portal?email=${encodeURIComponent(registerEmail)}`);
            }
          } catch (loginError) {
            setLoginError('Auto-login failed. Please log in manually.');
          }
        }, 1500);
      }
    } catch (error) {
      setRegisterError(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setAccountInfo(null);
    setOrders([]);
    setSelectedOrder(null);
    setLoginEmail('');
    setLoginPassword('');
    setActiveTab('dashboard');
    window.history.replaceState({}, '', '/portal');
  };

  // Handle forgot password
  const handleForgotPassword = async (e) => {
    e?.preventDefault();
    setForgotError('');
    setForgotMessage('');
    
    if (!forgotEmail) {
      setForgotError('Please enter your email address');
      return;
    }
    
    setForgotLoading(true);
    
    try {
      const response = await axios.post(`${API}/portal/forgot-password`, {
        email: forgotEmail.trim()
      });
      
      if (response.data.success) {
        setForgotMessage('Password reset instructions have been sent to your email.');
      }
    } catch (error) {
      setForgotError(error.response?.data?.detail || 'Failed to send reset email. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  // Fetch messages for selected order
  const fetchMessages = async (orderId) => {
    try {
      const response = await axios.get(`${API}/portal/messages/${orderId}?email=${encodeURIComponent(accountInfo?.email || loginEmail)}`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  // Fetch DTC requests for selected order
  const fetchDtcRequests = async (orderId) => {
    setLoadingDtcRequests(true);
    try {
      const response = await axios.get(`${API}/portal/dtc-requests/${orderId}?email=${encodeURIComponent(accountInfo?.email || loginEmail)}`);
      setDtcRequests(response.data.dtc_requests || []);
    } catch (error) {
      console.error('Error fetching DTC requests:', error);
      setDtcRequests([]);
    } finally {
      setLoadingDtcRequests(false);
    }
  };

  // Submit DTC removal request
  const submitDtcRequest = async () => {
    if (!dtcRequestCodes.trim() || !selectedOrder) return;
    
    setSubmittingDtcRequest(true);
    try {
      const response = await axios.post(`${API}/portal/dtc-request`, {
        order_id: selectedOrder.id,
        email: accountInfo?.email || loginEmail,
        dtc_codes: dtcRequestCodes.trim(),
        notes: dtcRequestNotes.trim()
      });
      
      if (response.data.success) {
        alert(`DTC removal request submitted!\n\nCodes: ${response.data.dtc_codes.join(', ')}\n\nOur team will process it shortly.`);
        setDtcRequestCodes('');
        setDtcRequestNotes('');
        // Refresh DTC requests
        fetchDtcRequests(selectedOrder.id);
        // Also refresh messages as a message is created
        fetchMessages(selectedOrder.id);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to submit DTC request';
      alert(errorMsg);
    } finally {
      setSubmittingDtcRequest(false);
    }
  };

  // Send message
  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedOrder) return;
    
    setSendingMessage(true);
    try {
      await axios.post(`${API}/portal/message`, {
        order_id: selectedOrder.id,
        email: accountInfo?.email || loginEmail,
        message: newMessage.trim(),
        sender: 'customer'
      });
      
      setMessages([...messages, {
        id: Date.now(),
        message: newMessage.trim(),
        sender: 'customer',
        created_at: new Date().toISOString()
      }]);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  // Fetch orders helper function
  const fetchOrders = async () => {
    try {
      const response = await axios.post(`${API}/portal/login-email`, { 
        email: accountInfo?.email || loginEmail 
      });
      if (response.data.success) {
        setOrders(response.data.orders || []);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  // Upload additional file
  const uploadFile = async (file) => {
    if (!file || !selectedOrder) return;
    
    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('order_id', selectedOrder.id);
    formData.append('email', accountInfo?.email || loginEmail);
    
    try {
      await axios.post(`${API}/portal/upload-file`, formData);
      alert('File uploaded successfully!');
      // Refresh order
      const response = await axios.post(`${API}/portal/login-email`, { email: accountInfo?.email || loginEmail });
      if (response.data.success) {
        setOrders(response.data.orders || []);
        const updated = response.data.orders.find(o => o.id === selectedOrder.id);
        if (updated) setSelectedOrder(updated);
      }
    } catch (error) {
      alert('Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  // Submit new order from portal
  const submitNewOrder = async () => {
    if (!fileId && !newOrderFile) {
      alert('Please upload and analyze a file first');
      return;
    }
    if (newOrderServices.length === 0) {
      alert('Please select at least one service');
      return;
    }
    
    setSubmittingOrder(true);
    
    // Build vehicle info
    let vehicleInfo = {};
    if (isManualVehicle) {
      vehicleInfo = {
        vehicle_make: manualVehicle.make,
        vehicle_model: manualVehicle.model,
        vehicle_year: manualVehicle.year,
        engine: manualVehicle.engine,
        ecu: getEcuName(),
        type: 'Other'
      };
    } else {
      const vType = vehicleTypes.find(v => v.id === selectedVehicleType);
      const manufacturer = manufacturers.find(m => m.id === selectedManufacturer);
      const model = models.find(m => m.id === selectedModel);
      const engine = engines.find(e => e.id === selectedEngine);
      
      vehicleInfo = {
        vehicle_make: manufacturer?.name || '',
        vehicle_model: model?.name || '',
        vehicle_year: '', // Year extracted from engine/model if available
        engine: engine?.name || '',
        ecu: getEcuName(),
        type: vType?.name || ''
      };
    }
    
    const formData = new FormData();
    if (newOrderFile) {
      formData.append('file', newOrderFile);
    }
    formData.append('file_id', fileId || '');
    formData.append('email', accountInfo?.email || loginEmail);
    formData.append('name', accountInfo?.name || 'Customer');
    
    // Build services array with full details including prices
    const servicesWithPrices = newOrderServices.map(serviceId => {
      const detected = detectedServices.find(s => s.service_id === serviceId);
      const available = availableServices.find(s => s.id === serviceId);
      return {
        id: serviceId,
        name: detected?.service_name || available?.name || serviceId.replace('-', ' ').replace('_', ' '),
        price: detected?.price || available?.price || 0
      };
    });
    
    formData.append('services', JSON.stringify(servicesWithPrices));
    formData.append('notes', newOrderNotes);
    formData.append('vehicle', JSON.stringify(vehicleInfo));
    formData.append('additional_dtc_codes', additionalDtcCodes); // FREE DTCs with DPF/EGR/AdBlue
    
    try {
      const response = await axios.post(`${API}/portal/new-order`, formData);
      if (response.data.success) {
        alert('Order submitted successfully!');
        // Refresh orders
        const ordersResponse = await axios.post(`${API}/portal/login-email`, { email: accountInfo?.email || loginEmail });
        if (ordersResponse.data.success) {
          setOrders(ordersResponse.data.orders || []);
        }
        // Reset form
        resetNewOrder();
        setActiveTab('orders');
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to submit order');
    } finally {
      setSubmittingOrder(false);
    }
  };

  // Update profile
  const updateProfile = async () => {
    try {
      await axios.post(`${API}/portal/update-profile`, {
        email: accountInfo?.email,
        name: profileName
      });
      setAccountInfo({ ...accountInfo, name: profileName });
      setEditingProfile(false);
      setSettingsMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (error) {
      setSettingsMessage({ type: 'error', text: 'Failed to update profile' });
    }
  };

  // Change password
  const changePassword = async () => {
    if (newPassword !== confirmNewPassword) {
      setSettingsMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    if (newPassword.length < 6) {
      setSettingsMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }
    
    try {
      await axios.post(`${API}/portal/change-password`, {
        email: accountInfo?.email,
        current_password: currentPassword,
        new_password: newPassword
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setSettingsMessage({ type: 'success', text: 'Password changed successfully!' });
    } catch (error) {
      setSettingsMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to change password' });
    }
  };

  // Quick Order - submit with previous order details
  const handleQuickOrder = async () => {
    if (!quickOrderFile || !quickOrderPrevOrder) {
      alert('Please select a file and a previous order to use as template');
      return;
    }
    
    setQuickOrderLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', quickOrderFile);
      formData.append('customer_email', accountInfo?.email);
      formData.append('customer_name', accountInfo?.name);
      
      // Use vehicle info from previous order
      const prevVehicle = quickOrderPrevOrder.vehicle || {};
      formData.append('vehicle_type', prevVehicle.type || quickOrderPrevOrder.vehicle_info || '');
      formData.append('manufacturer', prevVehicle.vehicle_make || '');
      formData.append('model', prevVehicle.vehicle_model || '');
      formData.append('engine', prevVehicle.engine || '');
      formData.append('ecu_type', prevVehicle.ecu || '');
      
      // Use same services from previous order
      const services = quickOrderPrevOrder.services || quickOrderPrevOrder.selected_services?.map(s => s.id) || [];
      formData.append('services', JSON.stringify(services));
      formData.append('notes', `Quick Order based on Order #${quickOrderPrevOrder.id?.slice(-8)}`);
      
      const response = await axios.post(`${API}/submit-quick-order`, formData);
      
      if (response.data.success) {
        alert('Quick Order submitted successfully! We will process your file shortly.');
        setShowQuickOrder(false);
        setQuickOrderFile(null);
        setQuickOrderPrevOrder(null);
        // Refresh orders
        const ordersResponse = await axios.post(`${API}/portal/login-email`, { email: accountInfo?.email });
        if (ordersResponse.data.orders) {
          setOrders(ordersResponse.data.orders);
        }
      }
    } catch (error) {
      alert('Failed to submit quick order: ' + (error.response?.data?.detail || error.message));
    } finally {
      setQuickOrderLoading(false);
    }
  };

  // Get last completed order for quick order template
  const getLastCompletedOrder = () => {
    return orders.find(o => o.status === 'completed') || orders[0];
  };

  // Calculate totals for dashboard
  const totalOrders = orders.length;
  const pendingOrders = orders.filter(o => o.status === 'pending' || o.status === 'processing').length;
  const completedOrders = orders.filter(o => o.status === 'completed').length;
  const totalSpent = orders.reduce((sum, o) => sum + (o.total_amount || o.price || 0), 0);

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'bg-green-100 text-green-400';
      case 'processing': return 'bg-[#00F0FF]/20 text-[#00F0FF]';
      case 'pending': return 'bg-yellow-100 text-yellow-400';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-[#1A1A1A] text-[#888]';
    }
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <Helmet>
          <title>Customer Portal | ECU Flash Service</title>
          <meta name="description" content="Access your ECU Flash Service customer portal. View orders, download files, and communicate with our support team." />
          <link rel="canonical" href="https://ecuflashservice.com/portal" />
        </Helmet>
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0A0A0A] mb-4 ">
              <svg className="w-9 h-9 text-[#00F0FF]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-white">Customer Portal</h1>
            <p className="text-white/80">Access your orders, files, and account</p>
          </div>
          
          {showRegister ? (
            /* Registration Card */
            <div className="bg-[#0A0A0A] border border-white/10 p-8 ">
              <h2 className="text-xl font-bold text-white mb-6">Create Account</h2>
              
              {registerSuccess ? (
                <div className="bg-green-500/10 border border-green-500/30 rounded-none p-6 text-center">
                  <svg className="w-12 h-12 text-green-400 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="text-green-400 font-medium">Account created! Signing you in...</p>
                </div>
              ) : (
                <form onSubmit={handleRegister}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#888] mb-1">Full Name</label>
                      <input
                        type="text"
                        value={registerName}
                        onChange={(e) => setRegisterName(e.target.value)}
                        className="w-full bg-[#1A1A1A] border border-white/10 rounded-none px-4 py-3 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF]"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#888] mb-1">Email</label>
                      <input
                        type="email"
                        value={registerEmail}
                        onChange={(e) => setRegisterEmail(e.target.value)}
                        className="w-full bg-[#1A1A1A] border border-white/10 rounded-none px-4 py-3 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF]"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#888] mb-1">Password</label>
                      <div className="relative">
                        <input
                          type={showRegisterPassword ? "text" : "password"}
                          value={registerPassword}
                          onChange={(e) => setRegisterPassword(e.target.value)}
                          className="w-full bg-[#1A1A1A] border border-white/10 rounded-none px-4 py-3 pr-12 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] text-white"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-[#00F0FF] transition"
                        >
                          {showRegisterPassword ? (
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#888] mb-1">Confirm Password</label>
                      <div className="relative">
                        <input
                          type={showRegisterConfirm ? "text" : "password"}
                          value={registerConfirm}
                          onChange={(e) => setRegisterConfirm(e.target.value)}
                          className="w-full bg-[#1A1A1A] border border-white/10 rounded-none px-4 py-3 pr-12 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] text-white"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowRegisterConfirm(!showRegisterConfirm)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-[#00F0FF] transition"
                        >
                          {showRegisterConfirm ? (
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                    
                    {registerError && (
                      <div className="bg-red-500/10 border border-red-500/30 rounded-none p-3 text-red-400 text-sm">
                        {registerError}
                      </div>
                    )}
                    
                    <button
                      type="submit"
                      disabled={loginLoading}
                      className="w-full bg-green-500 hover:bg-green-600 text-white py-3 rounded-none font-semibold transition disabled:opacity-50"
                    >
                      {loginLoading ? 'Creating...' : 'Create Account'}
                    </button>
                  </div>
                </form>
              )}
              
              <div className="mt-6 pt-6 border-t border-white/10 text-center">
                <button
                  onClick={() => setShowRegister(false)}
                  className="text-[#00F0FF] hover:text-[#00F0FF] font-medium"
                >
                  ← Back to Sign In
                </button>
              </div>
            </div>
          ) : (
            /* Login Card */
            <div className="bg-[#0A0A0A] border border-white/10 p-8 ">
              <form onSubmit={handleLogin}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#888] mb-1">Email Address</label>
                    <input
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full bg-[#1A1A1A] border border-white/10 rounded-none px-4 py-3 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF]"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#888] mb-1">Password</label>
                    <div className="relative">
                      <input
                        type={showLoginPassword ? "text" : "password"}
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        placeholder="Enter your password"
                        className="w-full bg-[#1A1A1A] border border-white/10 rounded-none px-4 py-3 pr-12 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] text-white"
                      />
                      <button
                        type="button"
                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-[#00F0FF] transition"
                      >
                        {showLoginPassword ? (
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                    <p className="text-[#666] text-xs mt-1">Leave blank for quick access with email only</p>
                  </div>
                  
                  {/* Remember Me Checkbox */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="rememberMe"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="w-4 h-4 bg-[#1A1A1A] border border-white/10 rounded text-[#00F0FF] focus:ring-[#00F0FF] focus:ring-offset-0 cursor-pointer"
                    />
                    <label htmlFor="rememberMe" className="text-[#888] text-sm cursor-pointer select-none">
                      Remember me (save password)
                    </label>
                  </div>
                  
                  {loginError && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-none p-3 text-red-400 text-sm">
                      {loginError}
                    </div>
                  )}
                  
                  <button
                    type="submit"
                    disabled={loginLoading}
                    className="w-full bg-[#00F0FF] hover:bg-[#00F0FF] text-white py-3 rounded-none font-semibold transition disabled:opacity-50"
                  >
                    {loginLoading ? 'Signing In...' : 'Sign In'}
                  </button>
                  
                  {/* Forgot Password Link */}
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-[#00F0FF] hover:text-[#00F0FF] text-sm"
                    >
                      Forgot your password?
                    </button>
                  </div>
                </div>
              </form>
              
              <div className="mt-6 pt-6 border-t border-white/10 space-y-3">
                <button
                  onClick={() => setShowRegister(true)}
                  className="w-full bg-[#1A1A1A] hover:bg-gray-200 text-[#888] py-3 rounded-none transition"
                >
                  Create New Account
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="w-full text-[#666] hover:text-[#888] py-2 text-sm"
                >
                  ← Back to Home
                </button>
              </div>
            </div>
          )}
          
          {/* Forgot Password Modal */}
          {showForgotPassword && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-[#0A0A0A] p-8 max-w-md w-full mx-4 ">
                <h2 className="text-xl font-bold text-white mb-2">Reset Password</h2>
                <p className="text-[#666] text-sm mb-6">Enter your email and we'll send you instructions to reset your password.</p>
                
                <form onSubmit={handleForgotPassword}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#888] mb-1">Email Address</label>
                      <input
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="w-full bg-[#1A1A1A] border border-white/10 rounded-none px-4 py-3 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF]"
                        required
                      />
                    </div>
                    
                    {forgotError && (
                      <div className="bg-red-500/10 border border-red-500/30 rounded-none p-3 text-red-400 text-sm">
                        {forgotError}
                      </div>
                    )}
                    
                    {forgotMessage && (
                      <div className="bg-green-500/10 border border-green-500/30 rounded-none p-3 text-green-400 text-sm">
                        {forgotMessage}
                      </div>
                    )}
                    
                    <button
                      type="submit"
                      disabled={forgotLoading}
                      className="w-full bg-[#00F0FF] hover:bg-[#00F0FF] text-white py-3 rounded-none font-semibold transition disabled:opacity-50"
                    >
                      {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setForgotEmail('');
                        setForgotError('');
                        setForgotMessage('');
                      }}
                      className="w-full text-[#666] hover:text-[#888] py-2 text-sm"
                    >
                      ← Back to Sign In
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Main Portal UI
  return (
    <div className="min-h-screen bg-[#050505] flex">
      <Helmet>
        <title>Customer Portal | ECU Flash Service</title>
        <meta name="description" content="Access your ECU Flash Service customer portal. View orders, download files, and communicate with our support team." />
        <link rel="canonical" href="https://ecuflashservice.com/portal" />
      </Helmet>

      {/* Sidebar - Midnight Tuner Theme */}
      <aside className="w-64 bg-[#0A0A0A] border-r border-white/10 flex flex-col relative">
        {/* Accent line */}
        <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#00F0FF] via-[#00F0FF]/20 to-transparent" />
        
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-11 h-11 bg-gradient-to-br from-[#00F0FF]/20 to-[#00F0FF]/5 border border-[#00F0FF]/30 flex items-center justify-center">
                <svg className="w-6 h-6 text-[#00F0FF]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div className="absolute inset-0 bg-[#00F0FF]/20 blur-lg -z-10" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-white uppercase tracking-wide">ECU Flash</h1>
              <p className="text-xs text-[#00F0FF]/70">Customer Portal</p>
            </div>
          </div>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {[
            { id: 'dashboard', icon: '📊', label: 'Dashboard', color: 'cyan' },
            { id: 'orders', icon: '📦', label: 'My Orders', color: 'cyan' },
            { id: 'new-order', icon: '➕', label: 'New Order', color: 'cyan' },
            { id: 'files', icon: '📁', label: 'Files', color: 'cyan' },
            { id: 'messages', icon: '💬', label: 'Messages', color: 'cyan' },
            { id: 'payments', icon: '💳', label: 'Payments', color: 'cyan' },
            { id: 'settings', icon: '⚙️', label: 'Settings', color: 'cyan' },
          ].map(item => (
            <button
              key={item.id}
              onClick={() => {
                setActiveTab(item.id);
                if (item.id === 'messages' && selectedOrder) {
                  fetchMessages(selectedOrder.id);
                }
              }}
              data-testid={`nav-${item.id}`}
              className={`group w-full flex items-center gap-3 px-4 py-3 text-left transition-all duration-200 relative ${
                activeTab === item.id
                  ? item.color === 'red' 
                    ? 'bg-[#FF003C]/10 text-[#FF003C] border-l-2 border-[#FF003C]'
                    : 'bg-[#00F0FF]/10 text-[#00F0FF] border-l-2 border-[#00F0FF]'
                  : 'text-[#888] hover:bg-[#1A1A1A] hover:text-white border-l-2 border-transparent'
              }`}
            >
              <span className="text-xl">{item.icon}</span>
              <span className="font-medium tracking-wide">{item.label}</span>
              {activeTab === item.id && (
                <div className={`absolute right-3 w-1.5 h-1.5 rounded-full ${item.color === 'red' ? 'bg-[#FF003C]' : 'bg-[#00F0FF]'}`} />
              )}
            </button>
          ))}
        </nav>
        
        {/* User Info */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#00F0FF]/20 to-[#00F0FF]/5 border border-[#00F0FF]/30 flex items-center justify-center text-[#00F0FF] font-bold">
              {accountInfo?.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-white truncate">{accountInfo?.name || 'Guest'}</p>
              <p className="text-xs text-[#666] truncate">{accountInfo?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full text-[#666] hover:text-[#FF003C] text-sm py-2 transition flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Sign Out
          </button>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="flex-1 overflow-auto bg-[#050505]">
        <div className="p-8">
          {/* Dashboard */}
          {activeTab === 'dashboard' && (
            <div>
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="font-heading text-3xl font-bold text-white uppercase tracking-wide">Dashboard</h2>
                  <p className="text-[#888] text-sm mt-1">Welcome back, {accountInfo?.name?.split(' ')[0] || 'Guest'}</p>
                </div>
                <div className="text-right">
                  <p className="text-[#666] text-xs">Last login</p>
                  <p className="text-[#888] text-sm">{new Date().toLocaleDateString()}</p>
                </div>
              </div>
              
              {/* Stats - Compact */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <div className="group relative bg-gradient-to-b from-[#1A1A1A] to-[#0A0A0A] p-4 border border-white/10 hover:border-[#00F0FF]/30 transition-all">
                  <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-[#00F0FF]/50 to-transparent" />
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[#666] text-xs mb-1">Total Orders</p>
                      <p className="text-2xl font-bold text-white">{totalOrders}</p>
                    </div>
                    <div className="w-8 h-8 bg-[#00F0FF]/10 flex items-center justify-center text-sm">📦</div>
                  </div>
                </div>
                <div className="group relative bg-gradient-to-b from-[#00F0FF]/10 to-[#0A0A0A] p-4 border border-[#00F0FF]/30 hover:border-[#00F0FF]/50 transition-all">
                  <div className="absolute top-0 left-0 w-full h-0.5 bg-[#00F0FF]" />
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[#00F0FF]/70 text-xs mb-1">In Progress</p>
                      <p className="text-2xl font-bold text-[#00F0FF]">{pendingOrders}</p>
                    </div>
                    <div className="w-8 h-8 bg-[#00F0FF]/20 flex items-center justify-center text-sm">⏳</div>
                  </div>
                </div>
                <div className="group relative bg-gradient-to-b from-[#1A1A1A] to-[#0A0A0A] p-4 border border-white/10 hover:border-green-500/30 transition-all">
                  <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-green-500/50 to-transparent" />
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[#666] text-xs mb-1">Completed</p>
                      <p className="text-2xl font-bold text-green-400">{completedOrders}</p>
                    </div>
                    <div className="w-8 h-8 bg-green-500/10 flex items-center justify-center text-sm">✅</div>
                  </div>
                </div>
                <div className="group relative bg-gradient-to-b from-[#1A1A1A] to-[#0A0A0A] p-4 border border-white/10 hover:border-[#00F0FF]/30 transition-all">
                  <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-[#00F0FF]/50 to-transparent" />
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[#666] text-xs mb-1">Total Spent</p>
                      <p className="text-2xl font-bold text-white">${totalSpent.toFixed(0)}</p>
                    </div>
                    <div className="w-8 h-8 bg-[#00F0FF]/10 flex items-center justify-center text-sm">💰</div>
                  </div>
                </div>
              </div>
              
              {/* Quick Actions - Compact */}
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-sm font-semibold text-white uppercase tracking-wide">Quick Actions</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {/* Quick Order - Only show if user has previous orders */}
                {orders.length > 0 && (
                  <button
                    onClick={() => {
                      setQuickOrderPrevOrder(getLastCompletedOrder());
                      setShowQuickOrder(true);
                    }}
                    className="group relative bg-gradient-to-br from-[#00F0FF] to-[#00D4AA] text-black p-4 text-left hover:shadow-[0_0_30px_rgba(0,240,255,0.4)] transition-all overflow-hidden"
                    data-testid="quick-order-btn"
                  >
                    <div className="absolute top-0 right-0 w-16 h-16 bg-white/20 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <span className="text-2xl mb-1 block">⚡</span>
                    <span className="font-heading font-bold block uppercase tracking-wide text-sm">Quick Order</span>
                    <span className="text-xs opacity-80">Same vehicle</span>
                  </button>
                )}
                <button
                  onClick={() => setActiveTab('new-order')}
                  className="group relative bg-gradient-to-br from-[#00F0FF]/80 to-[#00F0FF]/50 text-black p-4 text-left hover:shadow-[0_0_30px_rgba(0,240,255,0.4)] transition-all overflow-hidden"
                  data-testid="new-order-btn"
                >
                  <div className="absolute top-0 right-0 w-16 h-16 bg-white/20 rounded-full -translate-y-1/2 translate-x-1/2" />
                  <span className="text-2xl mb-1 block">📤</span>
                  <span className="font-heading font-bold block uppercase tracking-wide text-sm">Upload File</span>
                  <span className="text-xs opacity-80">New order</span>
                </button>
                <button
                  onClick={() => setActiveTab('orders')}
                  className="group relative bg-[#0A0A0A] border border-white/10 p-4 text-left hover:border-[#00F0FF]/50 hover:bg-[#0A0A0A]/80 transition-all"
                  data-testid="view-orders-btn"
                >
                  <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#00F0FF]/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="text-2xl mb-1 block">📋</span>
                  <span className="font-heading font-bold text-white block uppercase tracking-wide text-sm">Orders</span>
                  <span className="text-xs text-[#666]">Check status</span>
                </button>
                <button
                  onClick={() => setActiveTab('messages')}
                  className="group relative bg-[#0A0A0A] border border-white/10 p-4 text-left hover:border-[#00F0FF]/50 hover:bg-[#0A0A0A]/80 transition-all"
                  data-testid="messages-btn"
                >
                  <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#00F0FF]/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="text-2xl mb-1 block">💬</span>
                  <span className="font-heading font-bold text-white block uppercase tracking-wide text-sm">Messages</span>
                  <span className="text-xs text-[#666]">Support</span>
                </button>
              </div>
              
              {/* Recent Orders - Compact */}
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-sm font-semibold text-white uppercase tracking-wide">Recent Orders</h3>
                {orders.length > 0 && (
                  <button 
                    onClick={() => setActiveTab('orders')}
                    className="text-[#00F0FF] text-xs hover:text-white transition"
                  >
                    View All →
                  </button>
                )}
              </div>
              {orders.length === 0 ? (
                <div className="relative bg-[#0A0A0A] p-12 border border-white/10 text-center overflow-hidden">
                  <div className="absolute inset-0 grid-bg opacity-5" />
                  <div className="relative z-10">
                    <div className="w-16 h-16 mx-auto mb-4 bg-[#1A1A1A] border border-white/10 flex items-center justify-center">
                      <span className="text-3xl">📦</span>
                    </div>
                    <p className="text-[#888] mb-4">No orders yet</p>
                    <button
                      onClick={() => setActiveTab('new-order')}
                      className="bg-[#00F0FF] hover:bg-[#00F0FF]/80 text-black font-bold px-6 py-3 transition"
                    >
                      Create Your First Order
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-[#0A0A0A] border border-white/10 overflow-hidden">
                  {orders.slice(0, 5).map((order, idx) => (
                    <div
                      key={order.id}
                      className="group flex items-center justify-between p-4 border-b border-white/5 last:border-0 hover:bg-[#1A1A1A] cursor-pointer transition-all"
                      onClick={() => { setSelectedOrder(order); setActiveTab('orders'); }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-[#1A1A1A] border border-white/10 flex items-center justify-center text-[#888] text-xs group-hover:border-[#00F0FF]/30 transition">
                          #{idx + 1}
                        </div>
                        <div>
                          <p className="font-medium text-white text-sm group-hover:text-[#00F0FF] transition">
                            Order #{order.order_number || order.id?.slice(-8)}
                          </p>
                          <p className="text-sm text-[#666]">{order.vehicle_info || order.services?.join(', ') || 'ECU Service'}</p>
                        </div>
                      </div>
                      <div className="text-right flex items-center gap-4">
                        <div>
                          <span className={`px-3 py-1 text-xs font-medium ${getStatusColor(order.status)}`}>
                            {order.status || 'Pending'}
                          </span>
                          <p className="text-sm text-[#666] mt-1">{new Date(order.created_at).toLocaleDateString()}</p>
                        </div>
                        <svg className="w-5 h-5 text-[#666] group-hover:text-[#00F0FF] transition" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-3xl font-bold text-white uppercase tracking-wide">My Orders</h2>
                <button
                  onClick={() => setActiveTab('new-order')}
                  className="bg-[#00F0FF] hover:bg-[#00F0FF]/80 text-black font-bold px-4 py-2 transition flex items-center gap-2"
                >
                  <span>+</span> New Order
                </button>
              </div>
              
              {selectedOrder ? (
                /* Order Detail View */
                <div>
                  <button
                    onClick={() => setSelectedOrder(null)}
                    className="text-[#00F0FF] hover:text-white mb-4 flex items-center gap-2 transition"
                  >
                    ← Back to Orders
                  </button>
                  
                  <div className="bg-[#0A0A0A] border border-white/10 p-6 mb-6">
                    <div className="flex justify-between items-start mb-6">
                      <div>
                        <h3 className="text-xl font-bold text-white">Order #{selectedOrder.id?.slice(-8)}</h3>
                        <p className="text-[#666]">{new Date(selectedOrder.created_at).toLocaleString()}</p>
                      </div>
                      <span className={`px-4 py-2 rounded-full font-medium ${getStatusColor(selectedOrder.status)}`}>
                        {selectedOrder.status || 'Pending'}
                      </span>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-white mb-2">Services</h4>
                        <ul className="space-y-1">
                          {(selectedOrder.services || selectedOrder.selected_services || ['ECU Service']).map((s, i) => (
                            <li key={i} className="text-[#888]">• {typeof s === 'object' ? s.name : s}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-2">Vehicle</h4>
                        <p className="text-[#888]">{selectedOrder.vehicle_info || 'Not specified'}</p>
                      </div>
                    </div>
                    
                    <div className="mt-6 pt-6 border-t border-white/5">
                      <h4 className="font-semibold text-white mb-3">Files</h4>
                      <div className="space-y-2">
                        {selectedOrder.original_file && (
                          <div className="flex items-center justify-between bg-[#1A1A1A] rounded-none p-3">
                            <span className="text-[#888]">📄 Original File</span>
                            <a
                              href={`${API}/download/${selectedOrder.original_file}`}
                              className="text-[#00F0FF] hover:text-[#00F0FF] text-sm"
                              download
                            >
                              Download
                            </a>
                          </div>
                        )}
                        {selectedOrder.modified_file && (
                          <div className="flex items-center justify-between bg-green-50 rounded-none p-3">
                            <span className="text-green-400">✅ Modified File</span>
                            <a
                              href={`${API}/download/${selectedOrder.modified_file}`}
                              className="text-green-400 hover:text-green-400 text-sm font-medium"
                              download
                            >
                              Download
                            </a>
                          </div>
                        )}
                      </div>
                      
                      {/* Upload Additional File */}
                      <div className="mt-4">
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={(e) => uploadFile(e.target.files?.[0])}
                          className="hidden"
                        />
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingFile}
                          className="text-[#00F0FF] hover:text-[#00F0FF] text-sm"
                        >
                          {uploadingFile ? 'Uploading...' : '+ Upload Additional File'}
                        </button>
                      </div>
                    </div>
                    
                    {/* Payment Section - Show if payment is pending */}
                    {selectedOrder.payment_status !== 'paid' && selectedOrder.payment_status !== 'completed' && (
                      <div className="mt-6 pt-6 border-t border-white/5">
                        <h4 className="font-semibold text-white mb-3">💳 Payment Required</h4>
                        <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 mb-4">
                          <p className="text-yellow-400 text-sm">
                            Your order is pending payment. Please complete the payment to process your file.
                          </p>
                          <p className="text-white font-semibold text-lg mt-2">
                            Total: ${(selectedOrder.total_amount || selectedOrder.price || 0).toFixed(2)} USD
                          </p>
                        </div>
                        
                        {paymentSuccess ? (
                          <div className="bg-green-500/10 border border-green-500/30 p-4 text-center">
                            <span className="text-4xl mb-2 block">✅</span>
                            <p className="text-green-400 font-medium">Payment Successful!</p>
                            <p className="text-[#888] text-sm mt-1">Your order is now being processed.</p>
                          </div>
                        ) : paymentError ? (
                          <div className="bg-red-500/10 border border-red-500/30 p-4 mb-4">
                            <p className="text-red-400 text-sm">{paymentError}</p>
                            <button 
                              onClick={() => setPaymentError('')}
                              className="text-[#00F0FF] text-sm mt-2 hover:underline"
                            >
                              Try again
                            </button>
                          </div>
                        ) : (
                          <div className="bg-[#1A1A1A] border border-white/10 p-4">
                            <PayPalScriptProvider options={{ 
                              "client-id": PAYPAL_CLIENT_ID,
                              currency: "USD"
                            }}>
                              <PayPalButtons
                                style={{ 
                                  layout: "vertical",
                                  color: "blue",
                                  shape: "rect",
                                  label: "pay"
                                }}
                                disabled={processingPayment}
                                createOrder={(data, actions) => {
                                  const amount = selectedOrder.total_amount || selectedOrder.price || 0;
                                  return actions.order.create({
                                    purchase_units: [{
                                      description: `ECU Service Order #${selectedOrder.id?.slice(-8)}`,
                                      amount: {
                                        currency_code: "USD",
                                        value: amount.toFixed(2)
                                      }
                                    }]
                                  });
                                }}
                                onApprove={async (data, actions) => {
                                  setProcessingPayment(true);
                                  try {
                                    const details = await actions.order.capture();
                                    
                                    // Update order payment status in backend
                                    await axios.post(`${API}/portal/complete-payment`, {
                                      order_id: selectedOrder.id,
                                      email: accountInfo?.email || loginEmail,
                                      payment_id: details.id,
                                      payer_email: details.payer?.email_address,
                                      amount: selectedOrder.total_amount || selectedOrder.price || 0
                                    });
                                    
                                    setPaymentSuccess(true);
                                    
                                    // Refresh orders list
                                    setTimeout(() => {
                                      fetchOrders();
                                      setSelectedOrder(prev => ({...prev, payment_status: 'paid', status: 'processing'}));
                                    }, 2000);
                                    
                                  } catch (error) {
                                    console.error('Payment error:', error);
                                    setPaymentError('Payment processing failed. Please try again or contact support.');
                                  } finally {
                                    setProcessingPayment(false);
                                  }
                                }}
                                onError={(err) => {
                                  console.error('PayPal error:', err);
                                  setPaymentError('Payment failed. Please try again.');
                                }}
                                onCancel={() => {
                                  setPaymentError('Payment was cancelled.');
                                }}
                              />
                            </PayPalScriptProvider>
                            {processingPayment && (
                              <div className="text-center mt-4">
                                <p className="text-[#00F0FF] text-sm">Processing payment...</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Invoice Links */}
                    <div className="mt-4 pt-4 border-t border-white/5 space-y-2">
                      <button
                        onClick={() => {
                          const invoiceUrl = `${API}/portal/invoice/${selectedOrder.id}?email=${encodeURIComponent(accountInfo?.email)}`;
                          window.open(invoiceUrl, '_blank');
                        }}
                        className="text-[#00F0FF] hover:text-[#00F0FF]/80 text-sm flex items-center gap-1 transition"
                      >
                        📄 View Invoice (HTML)
                      </button>
                      <button
                        onClick={() => {
                          const pdfUrl = `${API}/portal/invoice/${selectedOrder.id}/pdf?email=${encodeURIComponent(accountInfo?.email)}`;
                          window.open(pdfUrl, '_blank');
                        }}
                        className="text-[#00F0FF] hover:text-[#00F0FF]/80 text-sm flex items-center gap-1 transition"
                      >
                        📥 Download Invoice (PDF)
                      </button>
                    </div>
                  </div>
                  
                  {/* DTC Removal Request Section - Only for completed/processing orders */}
                  {(selectedOrder.status === 'completed' || selectedOrder.status === 'processing' || selectedOrder.status === 'delivered' || selectedOrder.status === 'paid') && (
                    <div className="bg-[#0A0A0A] border border-[#00F0FF]/30 p-6 mb-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-[#00F0FF]/20 flex items-center justify-center">
                          <span className="text-xl">🔧</span>
                        </div>
                        <div>
                          <h4 className="font-heading font-semibold text-white uppercase tracking-wide">Request DTC Removal</h4>
                          <p className="text-xs text-[#00F0FF]">FREE follow-up service for this order</p>
                        </div>
                      </div>
                      
                      <div className="bg-[#1A1A1A] border border-white/10 p-4 mb-4">
                        <p className="text-[#888] text-sm">
                          After flashing the modified file, new DTCs may appear due to removed hardware (DPF, EGR, AdBlue, etc.). 
                          Enter the codes below and we'll remove them from your file - <span className="text-[#00F0FF] font-medium">FREE of charge</span>.
                        </p>
                      </div>
                      
                      {/* DTC Input */}
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-[#888] mb-2">DTC Codes to Remove</label>
                          <input
                            type="text"
                            value={dtcRequestCodes}
                            onChange={(e) => setDtcRequestCodes(e.target.value.toUpperCase())}
                            placeholder="P2002, P0401, P20EE, P2463..."
                            className="w-full bg-[#1A1A1A] border border-white/10 px-4 py-3 text-white placeholder-[#666] focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] font-mono"
                            data-testid="dtc-request-codes"
                          />
                          <p className="text-xs text-[#666] mt-1">Enter codes separated by commas (e.g., P2002, P0401)</p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-[#888] mb-2">Additional Notes (optional)</label>
                          <textarea
                            value={dtcRequestNotes}
                            onChange={(e) => setDtcRequestNotes(e.target.value)}
                            placeholder="Any additional information about the DTCs..."
                            rows={2}
                            className="w-full bg-[#1A1A1A] border border-white/10 px-4 py-3 text-white placeholder-[#666] focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] resize-none"
                            data-testid="dtc-request-notes"
                          />
                        </div>
                        
                        <button
                          onClick={submitDtcRequest}
                          disabled={submittingDtcRequest || !dtcRequestCodes.trim()}
                          className="w-full bg-[#00F0FF] hover:bg-[#00F0FF]/80 text-black font-bold py-3 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          data-testid="submit-dtc-request-btn"
                        >
                          {submittingDtcRequest ? (
                            <>
                              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                              </svg>
                              Submitting...
                            </>
                          ) : (
                            <>
                              <span>🔧</span>
                              Submit DTC Removal Request
                            </>
                          )}
                        </button>
                      </div>
                      
                      {/* Previous DTC Requests */}
                      {dtcRequests.length > 0 && (
                        <div className="mt-6 pt-6 border-t border-white/10">
                          <h5 className="text-sm font-medium text-[#888] mb-3">Previous DTC Requests</h5>
                          <div className="space-y-2">
                            {dtcRequests.map((req, idx) => (
                              <div key={idx} className={`p-3 border ${
                                req.status === 'completed' ? 'bg-green-500/10 border-green-500/30' :
                                req.status === 'processing' ? 'bg-[#00F0FF]/10 border-[#00F0FF]/30' :
                                'bg-[#1A1A1A] border-white/10'
                              }`}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-mono text-sm text-white">{req.dtc_codes?.join(', ')}</span>
                                  <span className={`text-xs px-2 py-0.5 ${
                                    req.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                    req.status === 'processing' ? 'bg-[#00F0FF]/20 text-[#00F0FF]' :
                                    'bg-yellow-500/20 text-yellow-400'
                                  }`}>
                                    {req.status || 'pending'}
                                  </span>
                                </div>
                                <p className="text-xs text-[#666]">{new Date(req.created_at).toLocaleString()}</p>
                                {req.processed_file && (
                                  <a 
                                    href={`${API}/portal/download/${selectedOrder.id}/${req.processed_file}?email=${encodeURIComponent(accountInfo?.email)}`}
                                    className="text-xs text-[#00F0FF] hover:underline mt-1 inline-block"
                                    download
                                  >
                                    ⬇️ Download Updated File
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Messages Section */}
                  <div className="bg-[#0A0A0A] border border-white/10 p-6">
                    <h4 className="font-semibold text-white mb-4">Messages</h4>
                    <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
                      {messages.length === 0 ? (
                        <p className="text-[#666] text-sm">No messages yet</p>
                      ) : (
                        messages.map((msg, i) => (
                          <div
                            key={i}
                            className={`p-3 rounded-none ${
                              msg.sender === 'customer' ? 'bg-[#00F0FF]/10 ml-8' : 'bg-[#1A1A1A] mr-8'
                            }`}
                          >
                            <p className="text-sm text-white">{msg.message}</p>
                            <p className="text-xs text-[#666] mt-1">
                              {msg.sender === 'customer' ? 'You' : 'Support'} • {new Date(msg.created_at).toLocaleString()}
                            </p>
                          </div>
                        ))
                      )}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type a message..."
                        className="flex-1 bg-[#1A1A1A] border border-white/10 rounded-none px-4 py-2 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF]"
                        onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                      />
                      <button
                        onClick={sendMessage}
                        disabled={sendingMessage || !newMessage.trim()}
                        className="bg-[#00F0FF] hover:bg-[#00F0FF] text-white px-4 py-2 rounded-none transition disabled:opacity-50"
                      >
                        Send
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                /* Orders List - Enhanced Table View */
                <div>
                  {/* Quick Filter Bar */}
                  <div className="bg-[#0A0A0A] border border-white/10 p-4 mb-4">
                    <div className="flex flex-wrap gap-4 items-center">
                      {/* Search Input */}
                      <div className="flex-1 min-w-[200px]">
                        <div className="relative">
                          <input
                            type="text"
                            value={orderFilter}
                            onChange={(e) => setOrderFilter(e.target.value)}
                            placeholder="Search by Order ID, Vehicle, or Service..."
                            className="w-full bg-[#1A1A1A] border border-white/10 pl-10 pr-4 py-2 text-white placeholder-[#666] focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF]"
                            data-testid="order-search"
                          />
                          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                      </div>
                      
                      {/* Status Filter */}
                      <div className="flex items-center gap-2">
                        <span className="text-[#666] text-sm">Status:</span>
                        <select
                          value={orderStatusFilter}
                          onChange={(e) => setOrderStatusFilter(e.target.value)}
                          className="bg-[#1A1A1A] border border-white/10 px-3 py-2 text-white text-sm focus:border-[#00F0FF]"
                          data-testid="order-status-filter"
                        >
                          <option value="all">All</option>
                          <option value="pending">Pending</option>
                          <option value="processing">Processing</option>
                          <option value="completed">Completed</option>
                          <option value="paid">Paid</option>
                        </select>
                      </div>
                      
                      {/* Order Count */}
                      <div className="text-[#666] text-sm">
                        {orders.filter(order => {
                          const matchesSearch = !orderFilter || 
                            order.id?.toLowerCase().includes(orderFilter.toLowerCase()) ||
                            order.vehicle_info?.toLowerCase().includes(orderFilter.toLowerCase()) ||
                            (order.services || []).some(s => (typeof s === 'object' ? s.name : s).toLowerCase().includes(orderFilter.toLowerCase()));
                          const matchesStatus = orderStatusFilter === 'all' || order.status?.toLowerCase() === orderStatusFilter.toLowerCase();
                          return matchesSearch && matchesStatus;
                        }).length} orders
                      </div>
                    </div>
                  </div>
                  
                  {/* Orders Table */}
                  <div className="bg-[#0A0A0A] border border-white/10 overflow-hidden">
                    {orders.length === 0 ? (
                      <div className="p-12 text-center">
                        <div className="w-16 h-16 mx-auto mb-4 bg-[#1A1A1A] border border-white/10 flex items-center justify-center">
                          <span className="text-3xl">📦</span>
                        </div>
                        <p className="text-[#888] mb-4">No orders found</p>
                        <button
                          onClick={() => setActiveTab('new-order')}
                          className="bg-[#00F0FF] hover:bg-[#00F0FF]/80 text-black font-bold px-6 py-3 transition"
                        >
                          Create New Order
                        </button>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-[#1A1A1A] border-b border-white/10">
                            <tr>
                              <th className="text-left px-6 py-4 text-xs font-semibold text-[#888] uppercase tracking-wider">ID</th>
                              <th className="text-left px-6 py-4 text-xs font-semibold text-[#888] uppercase tracking-wider">Created</th>
                              <th className="text-left px-6 py-4 text-xs font-semibold text-[#888] uppercase tracking-wider">Vehicle / Services</th>
                              <th className="text-left px-6 py-4 text-xs font-semibold text-[#888] uppercase tracking-wider">Status</th>
                              <th className="text-left px-6 py-4 text-xs font-semibold text-[#888] uppercase tracking-wider">Download</th>
                              <th className="text-left px-6 py-4 text-xs font-semibold text-[#888] uppercase tracking-wider"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-white/5">
                            {orders
                              .filter(order => {
                                const matchesSearch = !orderFilter || 
                                  order.id?.toLowerCase().includes(orderFilter.toLowerCase()) ||
                                  order.vehicle_info?.toLowerCase().includes(orderFilter.toLowerCase()) ||
                                  (order.services || []).some(s => (typeof s === 'object' ? s.name : s).toLowerCase().includes(orderFilter.toLowerCase()));
                                const matchesStatus = orderStatusFilter === 'all' || order.status?.toLowerCase() === orderStatusFilter.toLowerCase();
                                return matchesSearch && matchesStatus;
                              })
                              .map(order => (
                              <tr 
                                key={order.id} 
                                className="group hover:bg-[#1A1A1A]/50 cursor-pointer transition-colors"
                                onClick={() => { setSelectedOrder(order); fetchMessages(order.id); }}
                              >
                                <td className="px-6 py-4">
                                  <span className="font-mono text-sm text-[#00F0FF]">#{order.id?.slice(-8)}</span>
                                </td>
                                <td className="px-6 py-4">
                                  <div>
                                    <p className="text-sm text-white">{new Date(order.created_at).toLocaleDateString()}</p>
                                    <p className="text-xs text-[#666]">{new Date(order.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</p>
                                  </div>
                                </td>
                                <td className="px-6 py-4">
                                  <div>
                                    <p className="text-sm text-white">{order.vehicle_info || 'ECU File'}</p>
                                    <p className="text-xs text-[#666]">
                                      {(order.services || order.selected_services || ['ECU Service']).slice(0, 2).map(s => typeof s === 'object' ? s.name : s).join(', ')}
                                      {(order.services || order.selected_services || []).length > 2 && ` +${(order.services || order.selected_services).length - 2} more`}
                                    </p>
                                  </div>
                                </td>
                                <td className="px-6 py-4">
                                  <span className={`inline-flex items-center px-3 py-1 text-xs font-medium ${
                                    order.status === 'completed' || order.status === 'delivered' 
                                      ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                                      : order.status === 'processing' || order.status === 'in_progress'
                                        ? 'bg-[#00F0FF]/20 text-[#00F0FF] border border-[#00F0FF]/30'
                                        : order.status === 'paid'
                                          ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                          : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                                  }`}>
                                    {order.status === 'completed' || order.status === 'delivered' ? '✓ ' : ''}
                                    {order.status || 'Pending'}
                                  </span>
                                </td>
                                <td className="px-6 py-4">
                                  {order.modified_file ? (
                                    <span className="inline-flex items-center text-green-400 text-sm">
                                      <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                      Yes
                                    </span>
                                  ) : (
                                    <span className="text-[#666] text-sm">—</span>
                                  )}
                                </td>
                                <td className="px-6 py-4">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setSelectedOrder(order); fetchMessages(order.id); }}
                                    className="text-[#00F0FF] hover:text-white text-sm font-medium transition flex items-center gap-1 opacity-0 group-hover:opacity-100"
                                  >
                                    View
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        
                        {/* Empty state for filtered results */}
                        {orders.length > 0 && orders.filter(order => {
                          const matchesSearch = !orderFilter || 
                            order.id?.toLowerCase().includes(orderFilter.toLowerCase()) ||
                            order.vehicle_info?.toLowerCase().includes(orderFilter.toLowerCase()) ||
                            (order.services || []).some(s => (typeof s === 'object' ? s.name : s).toLowerCase().includes(orderFilter.toLowerCase()));
                          const matchesStatus = orderStatusFilter === 'all' || order.status?.toLowerCase() === orderStatusFilter.toLowerCase();
                          return matchesSearch && matchesStatus;
                        }).length === 0 && (
                          <div className="p-8 text-center">
                            <p className="text-[#666]">No orders match your filter</p>
                            <button 
                              onClick={() => { setOrderFilter(''); setOrderStatusFilter('all'); }}
                              className="text-[#00F0FF] text-sm mt-2 hover:underline"
                            >
                              Clear filters
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* New Order Tab - Enhanced with Vehicle Selection & Analysis */}
          {activeTab === 'new-order' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Create New Order</h2>
                {newOrderStep > 1 && (
                  <button
                    onClick={resetNewOrder}
                    className="text-[#666] hover:text-[#888] text-sm"
                  >
                    ← Start Over
                  </button>
                )}
              </div>

              {/* Progress Steps */}
              <div className="flex items-center justify-center mb-8">
                {['Vehicle', 'Upload', 'Analyze', 'Services'].map((label, i) => (
                  <div key={i} className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      newOrderStep > i + 1 ? 'bg-green-500 text-white' :
                      newOrderStep === i + 1 ? 'bg-[#00F0FF] text-white' :
                      'bg-gray-200 text-[#666]'
                    }`}>
                      {newOrderStep > i + 1 ? '✓' : i + 1}
                    </div>
                    <span className={`mx-2 text-sm ${newOrderStep === i + 1 ? 'text-white font-medium' : 'text-[#666]'}`}>
                      {label}
                    </span>
                    {i < 3 && <div className={`w-8 h-0.5 ${newOrderStep > i + 1 ? 'bg-green-500' : 'bg-gray-200'}`} />}
                  </div>
                ))}
              </div>
              
              <div className="max-w-2xl mx-auto">
                
                {/* Step 1: Vehicle Selection */}
                {newOrderStep === 1 && (
                  <div className="bg-[#0A0A0A] border border-white/10 p-6">
                    <h3 className="font-semibold text-white mb-4">Select Your Vehicle</h3>
                    
                    {/* Vehicle Type */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-[#888] mb-2">Vehicle Type</label>
                      <select
                        value={selectedVehicleType}
                        onChange={(e) => handleVehicleTypeChange(e.target.value)}
                        className="w-full bg-[#1A1A1A] border border-white/10 rounded-none px-4 py-3 focus:border-[#00F0FF]"
                      >
                        <option value="">Select vehicle type...</option>
                        {vehicleTypes.map(type => (
                          <option key={type.id} value={type.id}>{type.name}</option>
                        ))}
                        <option value="other">Other / Enter Manually</option>
                      </select>
                    </div>

                    {/* Manual Vehicle Entry */}
                    {isManualVehicle ? (
                      <div className="bg-orange-50 border border-orange-200 rounded-none p-4 mb-4">
                        <h4 className="font-medium text-orange-700 mb-3">Enter Vehicle Details</h4>
                        <div className="grid grid-cols-2 gap-3">
                          <input
                            type="text"
                            placeholder="Make (e.g., BMW)"
                            value={manualVehicle.make}
                            onChange={(e) => setManualVehicle({...manualVehicle, make: e.target.value})}
                            className="bg-white border border-white/10 rounded-none px-4 py-3"
                          />
                          <input
                            type="text"
                            placeholder="Model (e.g., 320d)"
                            value={manualVehicle.model}
                            onChange={(e) => setManualVehicle({...manualVehicle, model: e.target.value})}
                            className="bg-white border border-white/10 rounded-none px-4 py-3"
                          />
                          <input
                            type="text"
                            placeholder="Year (e.g., 2018)"
                            value={manualVehicle.year}
                            onChange={(e) => setManualVehicle({...manualVehicle, year: e.target.value})}
                            className="bg-white border border-white/10 rounded-none px-4 py-3"
                          />
                          <input
                            type="text"
                            placeholder="Engine (e.g., 2.0 Diesel)"
                            value={manualVehicle.engine}
                            onChange={(e) => setManualVehicle({...manualVehicle, engine: e.target.value})}
                            className="bg-white border border-white/10 rounded-none px-4 py-3"
                          />
                        </div>
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-[#888] mb-2">ECU Type</label>
                          <select
                            value={selectedEcu}
                            onChange={(e) => {
                              setSelectedEcu(e.target.value);
                              if (e.target.value !== 'other') setCustomEcu('');
                            }}
                            className="w-full bg-white border border-white/10 rounded-none px-4 py-3"
                          >
                            <option value="">Select ECU type...</option>
                            <optgroup label="⭐ Chinese Truck ECUs">
                              {commonEcuTypes.filter(e => ['Weichai', 'Cummins', 'Yuchai', 'FAW', 'Sinotruk', 'Dongfeng'].includes(e.manufacturer)).map((ecu) => (
                                <option key={ecu.id} value={ecu.id}>{ecu.name}</option>
                              ))}
                            </optgroup>
                            <optgroup label="Bosch">
                              {commonEcuTypes.filter(e => e.manufacturer === 'Bosch').map((ecu) => (
                                <option key={ecu.id} value={ecu.id}>{ecu.name}</option>
                              ))}
                            </optgroup>
                            <optgroup label="Siemens">
                              {commonEcuTypes.filter(e => e.manufacturer === 'Siemens').map((ecu) => (
                                <option key={ecu.id} value={ecu.id}>{ecu.name}</option>
                              ))}
                            </optgroup>
                            <optgroup label="Continental">
                              {commonEcuTypes.filter(e => e.manufacturer === 'Continental').map((ecu) => (
                                <option key={ecu.id} value={ecu.id}>{ecu.name}</option>
                              ))}
                            </optgroup>
                            <optgroup label="Delphi">
                              {commonEcuTypes.filter(e => e.manufacturer === 'Delphi').map((ecu) => (
                                <option key={ecu.id} value={ecu.id}>{ecu.name}</option>
                              ))}
                            </optgroup>
                            <optgroup label="Denso">
                              {commonEcuTypes.filter(e => e.manufacturer === 'Denso').map((ecu) => (
                                <option key={ecu.id} value={ecu.id}>{ecu.name}</option>
                              ))}
                            </optgroup>
                            <optgroup label="Marelli">
                              {commonEcuTypes.filter(e => e.manufacturer === 'Marelli').map((ecu) => (
                                <option key={ecu.id} value={ecu.id}>{ecu.name}</option>
                              ))}
                            </optgroup>
                            <optgroup label="Other">
                              <option value="other">Other (Enter manually)</option>
                            </optgroup>
                          </select>
                          
                          {/* Custom ECU Input for Manual Vehicle */}
                          {selectedEcu === 'other' && (
                            <input
                              type="text"
                              value={customEcu}
                              onChange={(e) => setCustomEcu(e.target.value)}
                              placeholder="Enter your ECU type (e.g., Bosch EDC17C49)"
                              className="w-full mt-3 bg-white border border-white/10 rounded-none px-4 py-3"
                            />
                          )}
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Manufacturer */}
                        {selectedVehicleType && manufacturers.length > 0 && (
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-[#888] mb-2">Manufacturer</label>
                            <select
                              value={selectedManufacturer}
                              onChange={(e) => handleManufacturerChange(e.target.value)}
                              className="w-full bg-[#1A1A1A] border border-white/10 rounded-none px-4 py-3"
                              disabled={vehicleLoading}
                            >
                              <option value="">Select manufacturer...</option>
                              {manufacturers.map(m => (
                                <option key={m.id} value={m.id}>{m.name}</option>
                              ))}
                            </select>
                          </div>
                        )}

                        {/* Model */}
                        {selectedManufacturer && models.length > 0 && (
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-[#888] mb-2">Model</label>
                            <select
                              value={selectedModel}
                              onChange={(e) => handleModelChange(e.target.value)}
                              className="w-full bg-[#1A1A1A] border border-white/10 rounded-none px-4 py-3"
                              disabled={vehicleLoading}
                            >
                              <option value="">Select model...</option>
                              {models.map(m => (
                                <option key={m.id} value={m.id}>{m.name}</option>
                              ))}
                            </select>
                          </div>
                        )}

                        {/* Engine - Shows directly after Model (no generations) */}
                        {selectedModel && engines.length > 0 && (
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-[#888] mb-2">Engine</label>
                            <select
                              value={selectedEngine}
                              onChange={(e) => handleEngineChange(e.target.value)}
                              className="w-full bg-[#1A1A1A] border border-white/10 rounded-none px-4 py-3"
                              disabled={vehicleLoading}
                            >
                              <option value="">Select engine...</option>
                              {engines.map(e => (
                                <option key={e.id} value={e.id}>{e.name}</option>
                              ))}
                            </select>
                          </div>
                        )}

                        {/* ECU Type - Dynamic based on engine selection */}
                        {selectedEngine && (
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-[#888] mb-2">
                              ECU Type
                              {ecuLoading && <span className="ml-2 text-xs text-[#666]">(Loading...)</span>}
                            </label>
                            <select
                              value={selectedEcu}
                              onChange={(e) => {
                                setSelectedEcu(e.target.value);
                                if (e.target.value !== 'other') setCustomEcu('');
                              }}
                              className="w-full bg-[#1A1A1A] border border-white/10 rounded-none px-4 py-3"
                              disabled={ecuLoading}
                            >
                              <option value="">Select ECU...</option>
                              
                              {/* Show dynamic ECUs from engine if available */}
                              {dynamicEcuTypes.length > 0 ? (
                                <>
                                  {dynamicEcuTypes.map((ecu) => (
                                    <option key={ecu.id} value={ecu.id}>{ecu.name}</option>
                                  ))}
                                  <option value="other">Other (Enter manually)</option>
                                </>
                              ) : (
                                /* Fallback to grouped commonEcuTypes if no dynamic ECUs */
                                <>
                                  <optgroup label="⭐ Chinese Truck ECUs">
                                    {commonEcuTypes.filter(e => ['Weichai', 'Cummins', 'Yuchai', 'FAW', 'Sinotruk', 'Dongfeng'].includes(e.manufacturer)).map((ecu) => (
                                      <option key={ecu.id} value={ecu.id}>{ecu.name}</option>
                                    ))}
                                  </optgroup>
                                  <optgroup label="Bosch">
                                    {commonEcuTypes.filter(e => e.manufacturer === 'Bosch').map((ecu) => (
                                      <option key={ecu.id} value={ecu.id}>{ecu.name}</option>
                                    ))}
                                  </optgroup>
                                  <optgroup label="Siemens">
                                    {commonEcuTypes.filter(e => e.manufacturer === 'Siemens').map((ecu) => (
                                      <option key={ecu.id} value={ecu.id}>{ecu.name}</option>
                                    ))}
                                  </optgroup>
                                  <optgroup label="Continental">
                                    {commonEcuTypes.filter(e => e.manufacturer === 'Continental').map((ecu) => (
                                      <option key={ecu.id} value={ecu.id}>{ecu.name}</option>
                                    ))}
                                  </optgroup>
                                  <optgroup label="Delphi">
                                    {commonEcuTypes.filter(e => e.manufacturer === 'Delphi').map((ecu) => (
                                      <option key={ecu.id} value={ecu.id}>{ecu.name}</option>
                                    ))}
                                  </optgroup>
                                  <optgroup label="Denso">
                                    {commonEcuTypes.filter(e => e.manufacturer === 'Denso').map((ecu) => (
                                      <option key={ecu.id} value={ecu.id}>{ecu.name}</option>
                                    ))}
                                  </optgroup>
                                  <optgroup label="Marelli">
                                    {commonEcuTypes.filter(e => e.manufacturer === 'Marelli').map((ecu) => (
                                      <option key={ecu.id} value={ecu.id}>{ecu.name}</option>
                                    ))}
                                  </optgroup>
                                  <optgroup label="Other">
                                    <option value="other">Other (Enter manually)</option>
                                  </optgroup>
                                </>
                              )}
                            </select>
                            
                            {/* Custom ECU Input */}
                            {selectedEcu === 'other' && (
                              <input
                                type="text"
                                value={customEcu}
                                onChange={(e) => setCustomEcu(e.target.value)}
                                placeholder="Enter your ECU type (e.g., Bosch EDC17C49)"
                                className="w-full mt-3 bg-[#1A1A1A] border border-white/10 rounded-none px-4 py-3"
                              />
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {/* Vehicle Summary */}
                    {isVehicleComplete() && (
                      <div className="bg-green-500/10 border border-green-500/30 rounded-none p-4 mb-4">
                        <div className="flex items-center gap-2 text-green-400">
                          <span>✓</span>
                          <span className="font-medium">{getVehicleSummary()}</span>
                          {getEcuName() && <span className="text-sm">({getEcuName()})</span>}
                        </div>
                      </div>
                    )}

                    <button
                      onClick={() => setNewOrderStep(2)}
                      disabled={!isVehicleComplete()}
                      className="w-full bg-[#00F0FF] hover:bg-[#00F0FF] disabled:bg-gray-300 text-white py-4 rounded-none font-semibold transition"
                    >
                      Continue to Upload →
                    </button>
                  </div>
                )}

                {/* Step 2: File Upload */}
                {newOrderStep === 2 && (
                  <div className="bg-[#0A0A0A] border border-white/10 p-6">
                    <h3 className="font-semibold text-white mb-2">Upload ECU File</h3>
                    <p className="text-sm text-[#666] mb-4">Vehicle: {getVehicleSummary()} ({getEcuName()})</p>
                    
                    <div
                      className={`border-2 border-dashed rounded-none p-8 text-center transition ${
                        newOrderFile ? 'border-green-400 bg-green-50' : 'border-white/10 hover:border-[#00F0FF]'
                      }`}
                    >
                      {newOrderFile ? (
                        <div>
                          <span className="text-4xl mb-2 block">✅</span>
                          <p className="font-medium text-white">{newOrderFile.name}</p>
                          <p className="text-sm text-[#666]">{(newOrderFile.size / 1024).toFixed(1)} KB</p>
                          <button
                            onClick={() => setNewOrderFile(null)}
                            className="text-red-400 hover:text-red-700 text-sm mt-2"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div>
                          <span className="text-4xl mb-2 block">📤</span>
                          <p className="text-[#888] mb-2">Drag & drop your ECU file here, or</p>
                          <label className="inline-block bg-[#00F0FF] hover:bg-[#00F0FF] text-white px-6 py-2 rounded-none cursor-pointer transition">
                            Browse Files
                            <input
                              type="file"
                              onChange={(e) => setNewOrderFile(e.target.files?.[0])}
                              className="hidden"
                              accept=".bin,.ori,.fls,.hex,.ecu,.mod,.mpc"
                            />
                          </label>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-3 mt-6">
                      <button
                        onClick={() => setNewOrderStep(1)}
                        className="flex-1 bg-[#1A1A1A] hover:bg-gray-200 text-[#888] py-4 rounded-none font-semibold transition"
                      >
                        ← Back
                      </button>
                      <button
                        onClick={() => setNewOrderStep(3)}
                        disabled={!newOrderFile}
                        className="flex-1 bg-[#00F0FF] hover:bg-[#00F0FF] disabled:bg-gray-300 text-white py-4 rounded-none font-semibold transition"
                      >
                        Analyze File →
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 3: Analysis */}
                {newOrderStep === 3 && (
                  <div className="bg-[#0A0A0A] border border-white/10 p-6">
                    <h3 className="font-semibold text-white mb-4">Analyzing ECU File</h3>
                    
                    <div className="text-center py-8">
                      {analyzing ? (
                        <div>
                          <div className="w-16 h-16 border-4 border-[#00F0FF] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                          <p className="text-[#888]">Analyzing your ECU file...</p>
                          <p className="text-sm text-[#666]">This may take a moment</p>
                        </div>
                      ) : (
                        <div>
                          <span className="text-5xl mb-4 block">🔍</span>
                          <p className="text-[#888] mb-4">Ready to analyze your ECU file</p>
                          <p className="text-sm text-[#666] mb-6">
                            File: {newOrderFile?.name}<br/>
                            Vehicle: {getVehicleSummary()}
                          </p>
                          <button
                            onClick={analyzeFile}
                            className="bg-[#00F0FF] hover:bg-[#00F0FF] text-white px-8 py-4 rounded-none font-semibold transition"
                          >
                            Start Analysis
                          </button>
                        </div>
                      )}
                    </div>

                    {!analyzing && (
                      <button
                        onClick={() => setNewOrderStep(2)}
                        className="w-full mt-4 bg-[#1A1A1A] hover:bg-gray-200 text-[#888] py-3 rounded-none font-medium transition"
                      >
                        ← Back to Upload
                      </button>
                    )}
                  </div>
                )}

                {/* Step 4: Service Selection */}
                {newOrderStep === 4 && (
                  <div>
                    {/* Analysis Result */}
                    {analysisResult && (
                      <div className="bg-gradient-to-r from-[#00F0FF] to-[#00F0FF] rounded-none p-4 mb-6 text-white">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold">{analysisResult.detected_manufacturer || 'ECU'} {analysisResult.detected_ecu || ''}</p>
                            <p className="text-sm text-white/80">{newOrderFile?.name}</p>
                          </div>
                          <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                            {detectedServices.length} detected
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {/* AdBlue/DCU Notice */}
                    <div className="bg-amber-50 border border-amber-200 rounded-none p-4 mb-4">
                      <div className="flex items-start gap-3">
                        <span className="text-amber-600">⚠️</span>
                        <div>
                          <h4 className="font-semibold text-amber-800 text-sm mb-1">AdBlue/SCR System Notice</h4>
                          <p className="text-amber-700 text-xs">
                            AdBlue systems are often controlled by a separate <strong>Dosing Control Unit (DCU)</strong>. 
                            If you need AdBlue removal, please upload both ECU and DCU files.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* FREE DTC Removal Input - Shows when DPF/EGR/AdBlue is selected */}
                    {newOrderServices.some(s => ['dpf-removal', 'egr-removal', 'adblue-removal', 'egr-dpf-combo'].includes(s)) && (
                      <div className="bg-green-500/10 border border-green-500/30 rounded-none p-4 mb-4">
                        <div className="flex items-start gap-3">
                          <span className="text-green-400 text-xl">✓</span>
                          <div className="flex-1">
                            <h4 className="font-semibold text-green-800 text-sm mb-1">FREE DTC Removal Included</h4>
                            <p className="text-green-400 text-xs mb-2">
                              All related DTCs are automatically removed with DPF/EGR/AdBlue services.
                            </p>
                            
                            {/* DTC Count & View/Select Button */}
                            {analysisResult?.detected_dtcs?.length > 0 && (
                              <div className="bg-white rounded-none p-3 border border-green-200 mb-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xl font-bold text-green-400">{analysisResult.detected_dtcs.length}</span>
                                    <span className="text-xs text-[#888]">DTCs found in file</span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setShowDtcSelector(!showDtcSelector)}
                                    className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-none text-xs font-medium transition"
                                  >
                                    <span>{showDtcSelector ? 'Hide' : 'View & Select'}</span>
                                    <svg className={`w-3 h-3 transition-transform ${showDtcSelector ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </button>
                                </div>
                                
                                {/* DTC Selection Grid */}
                                {showDtcSelector && (
                                  <div className="mt-3 border-t border-green-200 pt-3">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="text-xs font-medium text-[#888]">Select DTCs:</span>
                                      <div className="flex gap-2">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setSelectedFileDtcs(analysisResult.detected_dtcs.map(d => d.code));
                                            setAdditionalDtcCodes(analysisResult.detected_dtcs.map(d => d.code).join(', '));
                                          }}
                                          className="text-xs text-green-400 hover:text-green-400 font-medium"
                                        >
                                          Select All
                                        </button>
                                        <span className="text-gray-300">|</span>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setSelectedFileDtcs([]);
                                            setAdditionalDtcCodes('');
                                          }}
                                          className="text-xs text-[#666] hover:text-[#888] font-medium"
                                        >
                                          Clear
                                        </button>
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-1.5 max-h-40 overflow-y-auto">
                                      {analysisResult.detected_dtcs.map((dtc) => (
                                        <label
                                          key={dtc.code}
                                          className={`flex items-center gap-2 p-2 rounded-none border cursor-pointer transition text-xs ${
                                            selectedFileDtcs.includes(dtc.code)
                                              ? 'bg-green-100 border-green-400'
                                              : 'bg-[#1A1A1A] border-white/10 hover:border-green-300'
                                          }`}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={selectedFileDtcs.includes(dtc.code)}
                                            onChange={(e) => {
                                              let newSelected;
                                              if (e.target.checked) {
                                                newSelected = [...selectedFileDtcs, dtc.code];
                                              } else {
                                                newSelected = selectedFileDtcs.filter(c => c !== dtc.code);
                                              }
                                              setSelectedFileDtcs(newSelected);
                                              setAdditionalDtcCodes(newSelected.join(', '));
                                            }}
                                            className="h-3 w-3 text-green-400 border-white/10 rounded focus:ring-green-500"
                                          />
                                          <div className="flex-1 min-w-0">
                                            <span className="font-mono font-semibold text-white">{dtc.code}</span>
                                            <p className="text-[#666] truncate text-xs" title={dtc.description}>{dtc.description}</p>
                                          </div>
                                        </label>
                                      ))}
                                    </div>
                                    {selectedFileDtcs.length > 0 && (
                                      <div className="mt-2 p-2 bg-green-100 rounded-none text-xs text-green-400">
                                        <strong>{selectedFileDtcs.length}</strong> DTC(s) selected
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Manual DTC Input */}
                            <textarea
                              value={additionalDtcCodes}
                              onChange={(e) => setAdditionalDtcCodes(e.target.value.toUpperCase())}
                              placeholder={analysisResult?.detected_dtcs?.length > 0 ? "Or enter additional DTCs manually..." : "Additional DTCs (e.g., P0420, P0401)"}
                              rows={2}
                              className="w-full bg-white border border-green-200 rounded-none px-3 py-2 text-xs focus:border-green-500"
                            />
                            <p className="text-xs text-[#666] mt-1">💡 New DTCs after flashing? Contact us - FREE removal!</p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="bg-[#0A0A0A] border border-white/10 p-6 mb-6">
                      <h3 className="font-semibold text-white mb-4">Select Services</h3>
                      
                      {/* Detected Services */}
                      {detectedServices.length > 0 && (
                        <div className="mb-6">
                          <h4 className="text-sm font-medium text-green-400 mb-3 flex items-center gap-2">
                            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                            Detected Services
                          </h4>
                          <div className="space-y-2">
                            {detectedServices.map(service => (
                              <label
                                key={service.service_id}
                                className={`flex items-center justify-between p-4 rounded-none border cursor-pointer transition ${
                                  newOrderServices.includes(service.service_id)
                                    ? 'border-[#00F0FF] bg-[#00F0FF]/10'
                                    : 'border-white/10 hover:border-white/10'
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <input
                                    type="checkbox"
                                    checked={newOrderServices.includes(service.service_id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setNewOrderServices([...newOrderServices, service.service_id]);
                                      } else {
                                        setNewOrderServices(newOrderServices.filter(s => s !== service.service_id));
                                      }
                                    }}
                                    className="w-5 h-5 text-[#00F0FF]"
                                  />
                                  <div>
                                    <span className="font-medium text-white">{service.service_name}</span>
                                    <span className="ml-2 text-xs bg-green-100 text-green-400 px-2 py-0.5 rounded-full">Detected</span>
                                  </div>
                                </div>
                                <span className="font-semibold text-green-400">${service.price}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* All Available Services */}
                      <div>
                        <h4 className="text-sm font-medium text-[#888] mb-3 flex items-center gap-2">
                          <span className="w-2 h-2 bg-[#00F0FF] rounded-full"></span>
                          {detectedServices.length > 0 ? 'Additional Services' : 'Available Services'}
                        </h4>
                        <div className="grid grid-cols-2 gap-2">
                          {availableServices
                            .filter(s => !detectedServices.find(d => d.service_id === s.id))
                            .map(service => (
                            <label
                              key={service.id}
                              className={`flex items-center justify-between p-3 rounded-none border cursor-pointer transition ${
                                newOrderServices.includes(service.id)
                                  ? 'border-[#00F0FF] bg-[#00F0FF]/10'
                                  : 'border-white/10 hover:border-white/10'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={newOrderServices.includes(service.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setNewOrderServices([...newOrderServices, service.id]);
                                    } else {
                                      setNewOrderServices(newOrderServices.filter(s => s !== service.id));
                                    }
                                  }}
                                  className="w-4 h-4 text-[#00F0FF]"
                                />
                                <span className="text-sm font-medium text-white">{service.name}</span>
                              </div>
                              <span className="text-sm text-[#888]">${service.price}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Notes */}
                    <div className="bg-[#0A0A0A] border border-white/10 p-6 mb-6">
                      <h3 className="font-semibold text-white mb-4">Additional Notes (Optional)</h3>
                      <textarea
                        value={newOrderNotes}
                        onChange={(e) => setNewOrderNotes(e.target.value)}
                        placeholder="Any special instructions or requirements..."
                        rows={3}
                        className="w-full bg-[#1A1A1A] border border-white/10 rounded-none px-4 py-3 focus:border-[#00F0FF]"
                      />
                    </div>

                    {/* Order Summary */}
                    <div className="bg-gray-900 rounded-none p-6 text-white">
                      <h3 className="font-semibold mb-4">Order Summary</h3>
                      
                      <div className="text-sm text-[#666] mb-3">
                        Vehicle: {getVehicleSummary()}
                      </div>
                      
                      {newOrderServices.length > 0 ? (
                        <>
                          {newOrderServices.map(serviceId => {
                            const detected = detectedServices.find(s => s.service_id === serviceId);
                            const available = availableServices.find(s => s.id === serviceId);
                            const service = detected || available;
                            const name = detected?.service_name || available?.name;
                            const price = detected?.price || available?.price || 0;
                            
                            return service ? (
                              <div key={serviceId} className="flex justify-between text-sm mb-2">
                                <span className="text-gray-300">{name}</span>
                                <span>${price}</span>
                              </div>
                            ) : null;
                          })}
                          <div className="border-t border-gray-700 mt-4 pt-4 flex justify-between font-bold text-lg">
                            <span>Total</span>
                            <span>${newOrderServices.reduce((sum, id) => {
                              const detected = detectedServices.find(s => s.service_id === id);
                              const available = availableServices.find(s => s.id === id);
                              return sum + (detected?.price || available?.price || 0);
                            }, 0)}</span>
                          </div>
                        </>
                      ) : (
                        <p className="text-[#666]">No services selected</p>
                      )}
                      
                      <button
                        onClick={submitNewOrder}
                        disabled={submittingOrder || newOrderServices.length === 0}
                        className="w-full mt-6 bg-gradient-to-r from-[#00F0FF] to-[#00F0FF] hover:from-blue-600 hover:to-cyan-500 text-white py-4 rounded-none font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {submittingOrder ? 'Submitting...' : 'Submit Order'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* DTC Tool Tab */}
          {activeTab === 'dtc-tool' && (
            <div>
              <h2 className="text-2xl font-bold text-white mb-6">DTC Delete Tool</h2>
              
              <div className="bg-[#0A0A0A] border border-white/10 p-6 mb-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-[#00F0FF]/20 rounded-none flex items-center justify-center">
                    <span className="text-3xl">🔍</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white text-lg">Professional DTC Delete Service</h3>
                    <p className="text-[#666]">Remove diagnostic trouble codes from your ECU file</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-green-50 rounded-none p-4 text-center">
                    <p className="text-2xl font-bold text-green-400">$10</p>
                    <p className="text-sm text-[#888]">1 DTC Code</p>
                  </div>
                  <div className="bg-[#00F0FF]/10 rounded-none p-4 text-center">
                    <p className="text-2xl font-bold text-[#00F0FF]">$20</p>
                    <p className="text-sm text-[#888]">2-6 DTC Codes</p>
                  </div>
                  <div className="bg-purple-50 rounded-none p-4 text-center">
                    <p className="text-2xl font-bold text-purple-600">$30</p>
                    <p className="text-sm text-[#888]">7+ DTC Codes</p>
                  </div>
                </div>
                
                <div className="bg-[#1A1A1A] rounded-none p-4 mb-6">
                  <h4 className="font-medium text-white mb-2">✅ What&apos;s Included:</h4>
                  <ul className="text-sm text-[#888] space-y-1">
                    <li>• Permanent DTC code removal from ECU file</li>
                    <li>• Checksum correction included (+$5)</li>
                    <li>• Instant download after processing</li>
                    <li>• Support for all major ECU types</li>
                    <li>• Free re-processing if issues arise</li>
                  </ul>
                </div>
                
                <a
                  href="/tools/dtc-delete"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-[#00F0FF] hover:bg-[#00F0FF]/80 text-white py-3 px-6 rounded-none font-medium transition flex items-center justify-center gap-2"
                >
                  <span>Open DTC Delete Tool</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
              
              {/* Recent DTC Orders */}
              <div className="bg-[#0A0A0A] border border-white/10 p-6">
                <h3 className="font-semibold text-white mb-4">Your DTC Orders</h3>
                {orders.filter(o => o.services?.some(s => s.includes('dtc'))).length > 0 ? (
                  <div className="space-y-3">
                    {orders.filter(o => o.services?.some(s => s.includes('dtc'))).slice(0, 5).map(order => (
                      <div key={order.id} className="flex items-center justify-between p-3 bg-[#1A1A1A] rounded-none">
                        <div>
                          <p className="font-medium text-white">{order.vehicle_info?.make} {order.vehicle_info?.model}</p>
                          <p className="text-sm text-[#666]">{new Date(order.created_at).toLocaleDateString()}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          order.status === 'completed' ? 'bg-green-100 text-green-400' :
                          order.status === 'processing' ? 'bg-[#00F0FF]/20 text-[#00F0FF]' :
                          'bg-yellow-100 text-yellow-400'
                        }`}>
                          {order.status}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#666] text-center py-4">No DTC orders yet. Use the tool above to get started!</p>
                )}
              </div>
            </div>
          )}

          {/* Files Tab */}
          {activeTab === 'files' && (
            <div>
              <h2 className="text-2xl font-bold text-white mb-6">My Files</h2>
              
              <div className="bg-[#0A0A0A] border border-white/10 overflow-hidden">
                {orders.length === 0 ? (
                  <div className="p-8 text-center">
                    <p className="text-[#666]">No files yet. Create an order to upload files.</p>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-[#1A1A1A]">
                      <tr>
                        <th className="text-left px-6 py-4 text-sm font-medium text-[#666]">File</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-[#666]">Order</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-[#666]">Type</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-[#666]">Date</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-[#666]"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.flatMap(order => {
                        const files = [];
                        if (order.original_file) {
                          files.push({
                            name: order.original_filename || 'Original File',
                            type: 'Original',
                            file: order.original_file,
                            order: order,
                            date: order.created_at
                          });
                        }
                        if (order.modified_file) {
                          files.push({
                            name: order.modified_filename || 'Modified File',
                            type: 'Modified',
                            file: order.modified_file,
                            order: order,
                            date: order.completed_at || order.created_at
                          });
                        }
                        return files;
                      }).map((file, i) => (
                        <tr key={i} className="border-t border-white/5 hover:bg-[#1A1A1A]">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <span className="text-2xl">{file.type === 'Modified' ? '✅' : '📄'}</span>
                              <span className="font-medium text-white">{file.name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-[#666]">#{file.order.id?.slice(-8)}</td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              file.type === 'Modified' ? 'bg-green-100 text-green-400' : 'bg-[#1A1A1A] text-[#888]'
                            }`}>
                              {file.type}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-[#666]">{new Date(file.date).toLocaleDateString()}</td>
                          <td className="px-6 py-4">
                            <a
                              href={`${API}/download/${file.file}`}
                              className="text-[#00F0FF] hover:text-[#00F0FF] text-sm font-medium"
                              download
                            >
                              Download
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <div>
              <h2 className="text-2xl font-bold text-white mb-6">Messages</h2>
              
              <div className="grid md:grid-cols-3 gap-6">
                {/* Order List */}
                <div className="bg-[#0A0A0A] border border-white/10 overflow-hidden">
                  <div className="p-4 border-b border-white/5">
                    <h3 className="font-semibold text-white">Select Order</h3>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {orders.length === 0 ? (
                      <p className="p-4 text-[#666] text-sm">No orders</p>
                    ) : (
                      orders.map(order => (
                        <button
                          key={order.id}
                          onClick={() => { setSelectedOrder(order); fetchMessages(order.id); }}
                          className={`w-full text-left p-4 border-b border-white/5 hover:bg-[#1A1A1A] transition ${
                            selectedOrder?.id === order.id ? 'bg-[#00F0FF]/10' : ''
                          }`}
                        >
                          <p className="font-medium text-white">Order #{order.id?.slice(-8)}</p>
                          <p className="text-sm text-[#666]">{new Date(order.created_at).toLocaleDateString()}</p>
                        </button>
                      ))
                    )}
                  </div>
                </div>
                
                {/* Chat */}
                <div className="md:col-span-2 bg-[#0A0A0A] border border-white/10 overflow-hidden flex flex-col" style={{ height: '500px' }}>
                  {selectedOrder ? (
                    <>
                      <div className="p-4 border-b border-white/5 bg-[#1A1A1A]">
                        <p className="font-semibold text-white">Order #{selectedOrder.id?.slice(-8)}</p>
                      </div>
                      <div className="flex-1 overflow-y-auto p-4 space-y-3">
                        {messages.length === 0 ? (
                          <p className="text-[#666] text-center py-8">No messages yet. Start the conversation!</p>
                        ) : (
                          messages.map((msg, i) => (
                            <div
                              key={i}
                              className={`max-w-[80%] p-3 rounded-none ${
                                msg.sender === 'customer'
                                  ? 'bg-[#00F0FF] text-white ml-auto'
                                  : 'bg-[#1A1A1A] text-white'
                              }`}
                            >
                              <p>{msg.message}</p>
                              <p className={`text-xs mt-1 ${msg.sender === 'customer' ? 'text-blue-100' : 'text-[#666]'}`}>
                                {new Date(msg.created_at).toLocaleString()}
                              </p>
                            </div>
                          ))
                        )}
                      </div>
                      <div className="p-4 border-t border-white/5">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder="Type your message..."
                            className="flex-1 bg-[#1A1A1A] border border-white/10 rounded-none px-4 py-3 focus:border-[#00F0FF]"
                            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                          />
                          <button
                            onClick={sendMessage}
                            disabled={sendingMessage || !newMessage.trim()}
                            className="bg-[#00F0FF] hover:bg-[#00F0FF] text-white px-6 py-3 rounded-none transition disabled:opacity-50"
                          >
                            Send
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex-1 flex items-center justify-center text-[#666]">
                      Select an order to view messages
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Payments Tab */}
          {activeTab === 'payments' && (
            <div>
              <h2 className="text-2xl font-bold text-white mb-6">Payment History</h2>
              
              <div className="bg-[#0A0A0A] border border-white/10 overflow-hidden">
                {orders.length === 0 ? (
                  <div className="p-8 text-center">
                    <p className="text-[#666]">No payments yet</p>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-[#1A1A1A]">
                      <tr>
                        <th className="text-left px-6 py-4 text-sm font-medium text-[#666]">Date</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-[#666]">Order ID</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-[#666]">Description</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-[#666]">Amount</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-[#666]">Status</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-[#666]"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map(order => (
                        <tr key={order.id} className="border-t border-white/5 hover:bg-[#1A1A1A]">
                          <td className="px-6 py-4 text-sm text-[#666]">{new Date(order.created_at).toLocaleDateString()}</td>
                          <td className="px-6 py-4 font-mono text-sm">#{order.id?.slice(-8)}</td>
                          <td className="px-6 py-4 text-sm text-[#888]">
                            {(order.services || order.selected_services || []).slice(0, 2).map(s => typeof s === 'object' ? s.name : s).join(', ') || 'ECU Service'}
                          </td>
                          <td className="px-6 py-4 font-semibold">${(order.total_amount || order.price || 0).toFixed(2)}</td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              order.payment_status === 'paid' || order.payment_status === 'completed' ? 'bg-green-100 text-green-400' : 'bg-yellow-100 text-yellow-400'
                            }`}>
                              {order.payment_status === 'completed' ? 'Paid' : (order.payment_status || 'Pending')}
                            </span>
                          </td>
                          <td className="px-6 py-4 flex gap-2">
                            <button 
                              onClick={() => {
                                const invoiceUrl = `${BACKEND_URL}/api/portal/invoice/${order.id}?email=${encodeURIComponent(accountInfo?.email)}`;
                                window.open(invoiceUrl, '_blank');
                              }}
                              className="text-[#00F0FF] hover:text-[#00F0FF]/80 text-sm transition"
                              title="View Invoice"
                            >
                              📄 View
                            </button>
                            <button 
                              onClick={() => {
                                const pdfUrl = `${BACKEND_URL}/api/portal/invoice/${order.id}/pdf?email=${encodeURIComponent(accountInfo?.email)}`;
                                window.open(pdfUrl, '_blank');
                              }}
                              className="text-[#00F0FF] hover:text-[#00F0FF]/80 text-sm transition"
                              title="Download PDF Invoice"
                            >
                              📥 PDF
                            </button>
                            {order.payment_status !== 'paid' && order.payment_status !== 'completed' && (
                              <button 
                                onClick={() => {
                                  setSelectedOrder(order);
                                  setActiveTab('orders');
                                }}
                                className="text-green-400 hover:text-green-400 text-sm"
                              >
                                💳 Pay Now
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                
                {/* Total Summary */}
                {orders.length > 0 && (
                  <div className="p-6 bg-[#1A1A1A] border-t border-white/10">
                    <div className="flex justify-end gap-8">
                      <div className="text-right">
                        <p className="text-sm text-[#666]">Total Paid</p>
                        <p className="text-2xl font-bold text-white">${totalSpent.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div>
              <h2 className="text-2xl font-bold text-white mb-6">Account Settings</h2>
              
              <div className="max-w-2xl space-y-6">
                {/* Profile */}
                <div className="bg-[#0A0A0A] border border-white/10 p-6">
                  <h3 className="font-semibold text-white mb-4">Profile Information</h3>
                  
                  {settingsMessage.text && (
                    <div className={`mb-4 p-3 rounded-none text-sm ${
                      settingsMessage.type === 'success' ? 'bg-green-50 text-green-400' : 'bg-red-50 text-red-700'
                    }`}>
                      {settingsMessage.text}
                    </div>
                  )}
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#888] mb-1">Name</label>
                      {editingProfile ? (
                        <input
                          type="text"
                          value={profileName}
                          onChange={(e) => setProfileName(e.target.value)}
                          className="w-full bg-[#1A1A1A] border border-white/10 rounded-none px-4 py-3 focus:border-[#00F0FF]"
                        />
                      ) : (
                        <p className="text-white py-3">{accountInfo?.name}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#888] mb-1">Email</label>
                      <p className="text-white py-3">{accountInfo?.email}</p>
                      <p className="text-xs text-[#666]">Email cannot be changed</p>
                    </div>
                    
                    {editingProfile ? (
                      <div className="flex gap-3">
                        <button
                          onClick={updateProfile}
                          className="bg-[#00F0FF] hover:bg-[#00F0FF] text-white px-6 py-2 rounded-none transition"
                        >
                          Save Changes
                        </button>
                        <button
                          onClick={() => { setEditingProfile(false); setProfileName(accountInfo?.name || ''); }}
                          className="bg-[#1A1A1A] hover:bg-gray-200 text-[#888] px-6 py-2 rounded-none transition"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setEditingProfile(true)}
                        className="text-[#00F0FF] hover:text-[#00F0FF] font-medium"
                      >
                        Edit Profile
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Change Password */}
                <div className="bg-[#0A0A0A] border border-white/10 p-6">
                  <h3 className="font-semibold text-white mb-4">Change Password</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#888] mb-1">Current Password</label>
                      <input
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="w-full bg-[#1A1A1A] border border-white/10 rounded-none px-4 py-3 focus:border-[#00F0FF]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#888] mb-1">New Password</label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full bg-[#1A1A1A] border border-white/10 rounded-none px-4 py-3 focus:border-[#00F0FF]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#888] mb-1">Confirm New Password</label>
                      <input
                        type="password"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        className="w-full bg-[#1A1A1A] border border-white/10 rounded-none px-4 py-3 focus:border-[#00F0FF]"
                      />
                    </div>
                    <button
                      onClick={changePassword}
                      disabled={!currentPassword || !newPassword}
                      className="bg-[#00F0FF] hover:bg-[#00F0FF] text-white px-6 py-2 rounded-none transition disabled:opacity-50"
                    >
                      Update Password
                    </button>
                  </div>
                </div>
                
                {/* Danger Zone */}
                <div className="bg-[#0A0A0A] border border-red-200 p-6">
                  <h3 className="font-semibold text-red-400 mb-4">Danger Zone</h3>
                  <p className="text-[#888] text-sm mb-4">
                    Once you delete your account, there is no going back. Please be certain.
                  </p>
                  <button className="bg-red-50 hover:bg-red-100 text-red-400 border border-red-200 px-6 py-2 rounded-none transition">
                    Delete Account
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      
      {/* Quick Order Modal */}
      {showQuickOrder && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0A0A0A] border border-white/10 max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="quick-order-modal">
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <span>⚡</span> Quick Order
                  </h2>
                  <p className="text-[#666] text-sm mt-1">Reorder using your previous vehicle details</p>
                </div>
                <button
                  onClick={() => {
                    setShowQuickOrder(false);
                    setQuickOrderFile(null);
                  }}
                  className="text-[#666] hover:text-white p-2"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Previous Order Selection */}
              <div>
                <label className="block text-sm font-medium text-[#888] mb-2">Use Details From Previous Order</label>
                <select
                  value={quickOrderPrevOrder?.id || ''}
                  onChange={(e) => {
                    const order = orders.find(o => o.id === e.target.value);
                    setQuickOrderPrevOrder(order);
                  }}
                  className="w-full bg-[#1A1A1A] border border-white/10 rounded-none px-4 py-3 text-white focus:border-[#00F0FF]"
                >
                  {orders.map(order => (
                    <option key={order.id} value={order.id}>
                      Order #{order.id?.slice(-8)} - {order.vehicle?.vehicle_make || ''} {order.vehicle?.vehicle_model || order.vehicle_info || 'ECU Service'}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Vehicle Details Preview */}
              {quickOrderPrevOrder && (
                <div className="bg-[#1A1A1A] border border-white/10 p-4">
                  <h4 className="text-sm font-semibold text-[#00F0FF] mb-3">Vehicle Details (from previous order)</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-[#666]">Make:</span>
                      <span className="text-white ml-2">{quickOrderPrevOrder.vehicle?.vehicle_make || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-[#666]">Model:</span>
                      <span className="text-white ml-2">{quickOrderPrevOrder.vehicle?.vehicle_model || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-[#666]">Engine:</span>
                      <span className="text-white ml-2">{quickOrderPrevOrder.vehicle?.engine || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-[#666]">ECU:</span>
                      <span className="text-white ml-2">{quickOrderPrevOrder.vehicle?.ecu || 'N/A'}</span>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <span className="text-[#666] text-sm">Services:</span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {(quickOrderPrevOrder.selected_services || quickOrderPrevOrder.services || []).map((service, idx) => (
                        <span key={idx} className="bg-[#00F0FF]/10 text-[#00F0FF] px-2 py-1 text-xs">
                          {typeof service === 'string' ? service : service.name}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-[#888] mb-2">Upload New ECU File</label>
                <div 
                  className={`border-2 border-dashed rounded-none p-8 text-center cursor-pointer transition ${
                    quickOrderFile ? 'border-[#00F0FF] bg-[#00F0FF]/5' : 'border-white/20 hover:border-[#00F0FF]/50'
                  }`}
                  onClick={() => quickOrderFileRef.current?.click()}
                >
                  <input
                    ref={quickOrderFileRef}
                    type="file"
                    accept=".bin,.hex,.ecu,.ori,.mod"
                    onChange={(e) => setQuickOrderFile(e.target.files[0])}
                    className="hidden"
                  />
                  {quickOrderFile ? (
                    <div>
                      <span className="text-3xl mb-2 block">✅</span>
                      <p className="text-white font-medium">{quickOrderFile.name}</p>
                      <p className="text-[#666] text-sm">{(quickOrderFile.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                  ) : (
                    <div>
                      <span className="text-3xl mb-2 block">📁</span>
                      <p className="text-white font-medium">Click to select file</p>
                      <p className="text-[#666] text-sm">.bin, .hex, .ecu, .ori, .mod</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Footer Actions */}
            <div className="p-6 border-t border-white/10 flex gap-3">
              <button
                onClick={() => {
                  setShowQuickOrder(false);
                  setQuickOrderFile(null);
                }}
                className="flex-1 bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white py-3 rounded-none transition"
              >
                Cancel
              </button>
              <button
                onClick={handleQuickOrder}
                disabled={!quickOrderFile || quickOrderLoading}
                className="flex-1 bg-[#00F0FF] hover:bg-[#00D4E0] text-black font-semibold py-3 rounded-none transition disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-quick-order-btn"
              >
                {quickOrderLoading ? 'Submitting...' : 'Submit Quick Order'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerPortal;
